import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import {
  fmt,
  fmtB,
  fmtDate,
  type CipCostEntry,
  type CipProjectListRow,
  type CipRollforwardRow,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { AppModal } from '../components/AppModal';
import { ApprovalTimeline } from '../components/approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../components/approval/DecisionModal';
import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { SearchSelect } from '../components/SearchSelect';
import {
  useActiveCompanyId,
  useAddCipCostEntry,
  useApproveCipBorrowingRun,
  useApproveCipCapitalizeRequest,
  useCapitalizeCipProject,
  useCipBorrowingDisclosure,
  useCipBorrowingPreview,
  useCipBorrowingRuns,
  useCipCapRate,
  useCipCapitalizeRequests,
  useCipClearingInbox,
  useCipCostEntries,
  useCipProjects,
  useCipRetention,
  useCipRollforward,
  useCreateCipProject,
  useLoans,
  useMarkReadyCipProject,
  useReceiveFromClearing,
  useRejectCipBorrowingRun,
  useRejectCipCapitalizeRequest,
  useReleaseCipRetention,
  useResumeCipProject,
  useReturnCipBorrowingRun,
  useReturnCipCapitalizeRequest,
  useRunCipBorrowing,
  useSuspendCipProject,
  useWriteOffCipProject,
} from '../lib/queries';
import { exportRows, todayStamp, type ExportColumn } from '../lib/xlsx';

type TabKey = 'register' | 'detail' | 'inbox' | 'borrowing' | 'capitalize' | 'rollforward';
type CipStatus = 'planning' | 'active' | 'hold' | 'ready' | 'capitalized';
type PoolCode = 'DIRECT_EQ' | 'DIRECT_CONS' | 'INDIRECT' | 'BORROW' | 'OTHER';
type BadgeTone = 'neutral' | 'info' | 'warning' | 'positive' | 'brand';

interface StatusMeta {
  label: string;
  tone: string;
  badge: BadgeTone;
}

interface PoolMeta {
  code: PoolCode;
  label: string;
  tone: string;
}

interface CipProject {
  id: string;
  apiId: string;
  name: string;
  branch: string;
  pm: string;
  budget: number;
  started: string;
  expected: string;
  status: CipStatus;
  // Raw API status — 'hold' collapses SUSPENDED + CANCELLED, but only SUSPENDED
  // is resumable, so keep the source value to drive the suspend/resume action.
  apiStatus: CipProjectListRow['status'];
  costs: Record<PoolCode, number>;
  total: number;
  progress: number;
  daysActive: number;
  // Phase 5 (IAS 23.20) — populated only while the project is SUSPENDED ('hold'),
  // so the detail header can show why capitalization is paused.
  suspendReason?: string | null;
}

interface CostItem {
  date: string;
  ref: string;
  desc: string;
  vendor: string;
  pool: PoolCode;
  amt: number;
}

interface CipTotals {
  activeCount: number;
  readyCount: number;
  cipBalance: number;
  borrowCost: number;
  budget: number;
  avgProgress: number;
}

interface FaSplit {
  name: string;
  cat: string;
  life: number;
  amt: number;
}

const CIP_STATUS: Readonly<Record<CipStatus, StatusMeta>> = {
  planning: { label: 'วางแผน', tone: '#52525b', badge: 'neutral' },
  active: { label: 'กำลังก่อสร้าง', tone: '#1a56a6', badge: 'info' },
  hold: { label: 'ระงับชั่วคราว', tone: '#b54708', badge: 'warning' },
  ready: { label: 'พร้อมตั้งทรัพย์สิน', tone: '#117a3d', badge: 'positive' },
  capitalized: { label: 'โอนเป็นทรัพย์สินแล้ว', tone: '#7030A0', badge: 'brand' },
};

const COST_POOLS: readonly PoolMeta[] = [
  { code: 'DIRECT_EQ', label: 'Direct — เครื่องจักร/อุปกรณ์', tone: '#1a56a6' },
  { code: 'DIRECT_CONS', label: 'Direct — ก่อสร้าง/วัสดุ', tone: '#b54708' },
  { code: 'INDIRECT', label: 'Indirect — ดูแลโครงการ', tone: '#a16207' },
  { code: 'BORROW', label: 'Borrowing Cost (TFRS 23)', tone: '#7030A0' },
  { code: 'OTHER', label: 'อื่นๆ (ทดสอบ ขนส่ง อนุญาต)', tone: '#52525b' },
];

// Reference "now" for aging math (daysActive / agingDaysOver). Defaults to the
// LIVE current date so production aging advances with the calendar; tests inject
// a fixed clock via setCipRefNow() to stay deterministic. Mirrors the injectable-
// clock pattern used elsewhere rather than a frozen hardcoded constant.
let _refNowOverride: Date | null = null;

export function setCipRefNow(d: Date | null): void {
  _refNowOverride = d;
}

function cipRefNow(): Date {
  return _refNowOverride ?? new Date();
}

// Shared Thai month abbreviations ('MM' → label) for the borrowing-cost period
// pickers (tab + run-modal). Mirrors the rollforward tab's month list.
const THAI_MONTHS: readonly [string, string][] = [
  ['01', 'ม.ค.'],
  ['02', 'ก.พ.'],
  ['03', 'มี.ค.'],
  ['04', 'เม.ย.'],
  ['05', 'พ.ค.'],
  ['06', 'มิ.ย.'],
  ['07', 'ก.ค.'],
  ['08', 'ส.ค.'],
  ['09', 'ก.ย.'],
  ['10', 'ต.ค.'],
  ['11', 'พ.ย.'],
  ['12', 'ธ.ค.'],
];

function thaiMonthLabel(month: string): string {
  return THAI_MONTHS.find(([v]) => v === month)?.[1] ?? month;
}

// Phase 3b approval-gating status → Thai badge label + tone. Prefers the engine
// `approvalStatus` (PENDING/RETURNED) while in flight, then falls back to the
// terminal accounting `status` (POSTED/CAPITALIZED/CANCELLED). Shared by the
// borrowing-run + capitalize-request views.
function cipApprovalBadge(
  approvalStatus: string | null | undefined,
  accountingStatus: string | null | undefined,
): { label: string; tone: BadgeTone } {
  const eng = approvalStatus ?? '';
  if (eng === 'PENDING') return { label: 'รออนุมัติ', tone: 'warning' };
  if (eng === 'RETURNED') return { label: 'ตีกลับแก้ไข', tone: 'warning' };
  if (eng === 'REJECTED') return { label: 'ไม่อนุมัติ', tone: 'neutral' };
  switch (accountingStatus) {
    case 'POSTED':
      return { label: 'อนุมัติแล้ว · Post JV', tone: 'positive' };
    case 'CAPITALIZED':
      return { label: 'อนุมัติแล้ว · โอนทรัพย์สิน', tone: 'positive' };
    case 'CANCELLED':
      return { label: 'ยกเลิก', tone: 'neutral' };
    default:
      return { label: 'ฉบับร่าง', tone: 'info' };
  }
}

// Adapter — CipProjectListRow (API) → prototype CipProject DOM shape. Cost-pool
// decomposition isn't stored on the server yet; collapse accumulatedCost into
// DIRECT_EQ and surface borrowingCost separately so the matrix still renders.
function mapApiRow(r: CipProjectListRow): CipProject {
  const total = Number(r.accumulatedCost);
  const budget = Number(r.budget) || 0;
  let status: CipStatus = 'active';
  if (r.status === 'CAPITALIZED') status = 'capitalized';
  else if (r.status === 'CANCELLED') status = 'hold';
  else if (r.status === 'SUSPENDED') status = 'hold';
  else if (r.status === 'READY') status = 'ready';
  const started = new Date(r.startDate).toISOString().slice(0, 10);
  const expected = r.expectedCompletionDate
    ? new Date(r.expectedCompletionDate).toISOString().slice(0, 10)
    : started;
  const daysActive = Math.max(
    0,
    Math.floor((cipRefNow().getTime() - new Date(r.startDate).getTime()) / (1000 * 60 * 60 * 24)),
  );
  const pt = r.poolTotals;
  return {
    id: r.voucherNo,
    apiId: r.id,
    name: r.name,
    branch: r.branchName ?? '—',
    pm: r.pm ?? '—',
    budget,
    started,
    expected,
    status,
    apiStatus: r.status,
    costs: {
      DIRECT_EQ: Number(pt?.DIRECT_EQ ?? 0),
      DIRECT_CONS: Number(pt?.DIRECT_CONS ?? 0),
      INDIRECT: Number(pt?.INDIRECT ?? 0),
      BORROW: Number(pt?.BORROW ?? 0),
      OTHER: Number(pt?.OTHER ?? 0),
    },
    total,
    progress: budget > 0 ? Math.min(100, Math.round((total / budget) * 100)) : 0,
    daysActive,
    suspendReason: r.suspendReason ?? null,
  };
}

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

function getStatus(s: CipStatus): StatusMeta {
  return CIP_STATUS[s];
}

function getPool(c: PoolCode): PoolMeta {
  return COST_POOLS.find((p) => p.code === c) ?? COST_POOLS[4]!;
}

// CapitalizeModal <select> Thai labels → real AssetCategory codes (see seed).
const CAT_TO_CODE: Readonly<Record<string, string>> = {
  เครื่องจักร: 'MACHINE',
  อาคารและสิ่งปลูกสร้าง: 'BUILDING',
  ยานพาหนะ: 'VEHICLE',
  คอมพิวเตอร์: 'COMPUTER',
  เฟอร์นิเจอร์: 'FURNITURE',
};

function catToCode(label: string): string {
  return CAT_TO_CODE[label] ?? 'BUILDING';
}

// Days a still-open project is past its expected-completion date (0 if not over).
function agingDaysOver(p: CipProject): number {
  if (p.status !== 'active' && p.status !== 'ready') return 0;
  const exp = new Date(p.expected).getTime();
  if (Number.isNaN(exp)) return 0;
  const d = Math.floor((cipRefNow().getTime() - exp) / (1000 * 60 * 60 * 24));
  return d > 0 ? d : 0;
}

function isOverBudget(p: CipProject): boolean {
  return p.budget > 0 && p.total > p.budget;
}

// (Legacy mock project/loan/cap-rate/detail-item constants removed — the register
// + detail tabs now render from useCipProjects + mapApiRow + useCipCostEntries,
// and the borrowing tab from useLoans + useCipCapRate + useCipBorrowingPreview.)

export const Route = createFileRoute('/cip')({
  component: CipPage,
});

export function CipPage() {
  const companyId = useActiveCompanyId();
  const projectsQuery = useCipProjects(companyId);
  const projects = useMemo<readonly CipProject[]>(
    () => (projectsQuery.data ?? []).map(mapApiRow),
    [projectsQuery.data],
  );

  const [tab, setTab] = useState<TabKey>('register');
  const [sel, setSel] = useState<CipProject | null>(null);
  const [showCap, setShowCap] = useState<CipProject | null>(null);
  const [showNew, setShowNew] = useState<boolean>(false);
  const [showBorrow, setShowBorrow] = useState<boolean>(false);
  const [showWriteOff, setShowWriteOff] = useState<CipProject | null>(null);
  const [year, setYear] = useState('2026');
  // Borrowing-cost period month — defaults to the current calendar month so the
  // TFRS 23 tab + run-modal target the latest period out of the box.
  const [borrowMonth, setBorrowMonth] = useState<string>(
    String(new Date().getMonth() + 1).padStart(2, '0'),
  );

  const markReadyMut = useMarkReadyCipProject(companyId);
  const onMarkReady = (p: CipProject) => {
    if (!p.apiId || !companyId) return;
    markReadyMut.mutate({
      cipProjectId: p.apiId,
      data: { readyForUseDate: new Date().toISOString().slice(0, 10) },
    });
  };

  const totals = useMemo<CipTotals>(() => {
    const active = projects.filter(
      (p) => p.status === 'active' || p.status === 'planning' || p.status === 'hold',
    );
    const cipBalance = projects
      .filter((p) => p.status !== 'capitalized')
      .reduce((s, p) => s + p.total, 0);
    return {
      activeCount: active.length,
      readyCount: projects.filter((p) => p.status === 'ready').length,
      cipBalance,
      borrowCost: projects.reduce((s, p) => s + (p.costs.BORROW ?? 0), 0),
      budget: active.reduce((s, p) => s + p.budget, 0),
      avgProgress: Math.round(
        active.reduce((s, p) => s + p.progress, 0) / Math.max(active.length, 1),
      ),
    };
  }, [projects]);

  const readyAmt = projects.filter((p) => p.status === 'ready').reduce((s, p) => s + p.total, 0);
  const currentSel = sel ?? projects[0] ?? null;

  const exportSchedule = () => {
    const cols: ExportColumn<CipProject>[] = [
      { header: 'รหัส', value: (p) => p.id, width: 16 },
      { header: 'ชื่อโครงการ', value: (p) => p.name, width: 34 },
      { header: 'สาขา', value: (p) => p.branch, width: 22 },
      { header: 'ผู้จัดการ', value: (p) => p.pm, width: 20 },
      { header: 'งบประมาณ', value: (p) => p.budget, width: 16 },
      { header: 'CIP สะสม', value: (p) => p.total, width: 16 },
      { header: 'คืบหน้า %', value: (p) => p.progress, width: 10 },
      { header: 'คาดเสร็จ', value: (p) => p.expected, width: 12 },
      { header: 'สถานะ', value: (p) => getStatus(p.status).label, width: 16 },
    ];
    void exportRows(`CIP_Schedule_${todayStamp()}.xlsx`, 'CIP Schedule', cols, projects);
  };

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['register', 'ทะเบียนโครงการ (Project Register)'],
    ['detail', 'รายละเอียดรายโครงการ'],
    ['inbox', 'กล่องรับเข้า (AP)'],
    ['borrowing', 'คำนวณดอกเบี้ย TFRS 23'],
    ['capitalize', 'โอนเป็นทรัพย์สิน (Capitalization)'],
    ['rollforward', 'CIP Rollforward — สรุปยกยอด'],
  ];

  const concept: readonly { n: string; t: string; c: string; d: string }[] = [
    {
      n: '1',
      t: 'สร้าง Project + ผูกบัญชี 10260002',
      c: '#1a56a6',
      d: 'ทุก JV ในบัญชี CIP บังคับเลือก Project Code',
    },
    {
      n: '2',
      t: 'สะสม Direct + Indirect Cost',
      c: '#117a3d',
      d: 'Dr.10260002 / Cr.เจ้าหนี้ หรือ เงินทดรอง',
    },
    {
      n: '3',
      t: 'Auto Capitalize ดอกเบี้ย (TFRS 23)',
      c: '#7030A0',
      d: 'Dr.10260002 / Cr.60200004 ทุกสิ้นเดือนตาม Cap Rate',
    },
    {
      n: '4',
      t: 'โอนเป็นทรัพย์สิน เมื่อพร้อมใช้งาน',
      c: '#b54708',
      d: 'Dr.10220001 FA / Cr.10260002 + ลงทะเบียน FA module',
    },
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'งานระหว่างก่อสร้าง (CIP)']}
        title="งานระหว่างก่อสร้าง — CIP Management + TFRS 23"
        actions={
          <>
            <HelpButton guide="cip" />
            <select className="select" value={year} onChange={(e) => setYear(e.target.value)}>
              <option value="2026">ปี: 2026</option>
              <option value="2025">ปี: 2025</option>
            </select>
            <button className="btn" onClick={() => setShowBorrow(true)}>
              <Icon name="refresh" size={14} /> คำนวณดอกเบี้ยที่ Capitalize
            </button>
            <button className="btn" onClick={exportSchedule}>
              <Icon name="download" size={14} /> Export CIP Schedule
            </button>
            <button className="btn primary" onClick={() => setShowNew(true)}>
              <Icon name="plus" size={14} /> สร้าง Project ใหม่
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            CIP คงเหลือในบัญชี 10260002
          </div>
          <div className="value tnum">
            {fmtB(totals.cipBalance)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{totals.activeCount} โครงการกำลังก่อสร้าง</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: '#7030A0' }} />
            Borrowing Cost ใน CIP (สะสม)
          </div>
          <div className="value tnum">
            {fmtB(totals.borrowCost)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            TFRS 23 ·{' '}
            {totals.cipBalance ? ((totals.borrowCost / totals.cipBalance) * 100).toFixed(1) : '0.0'}
            % ของ CIP
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            พร้อมโอนเป็นทรัพย์สิน
          </div>
          <div className="value tnum">
            {totals.readyCount} <span className="unit">โครงการ</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> รวม {fmtB(readyAmt)} บาท
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            งบประมาณรวม
          </div>
          <div className="value tnum">
            {fmtB(totals.budget)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">ใช้ไป {totals.avgProgress}% ของงบเฉลี่ย</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          วงจรการบันทึก — Project as Cost Container
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {concept.map((s) => (
            <div
              key={s.n}
              style={{
                display: 'flex',
                gap: 10,
                padding: '10px',
                background: 'white',
                borderRadius: 6,
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: s.c,
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: 'var(--font-display)',
                  flexShrink: 0,
                }}
              >
                {s.n}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{s.t}</div>
                <div className="meta" style={{ marginTop: 3 }}>
                  {s.d}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <CipRegister
          projects={projects}
          year={year}
          isPending={projectsQuery.isPending}
          error={projectsQuery.error}
          onPick={(p) => {
            setSel(p);
            setTab('detail');
          }}
          onCap={(p) => setShowCap(p)}
          onMarkReady={onMarkReady}
          markReadyPending={markReadyMut.isPending}
        />
      )}
      {tab === 'detail' && currentSel ? (
        <CipDetail
          proj={currentSel}
          projects={projects}
          setProj={setSel}
          onCap={() => setShowCap(currentSel)}
          onMarkReady={() => onMarkReady(currentSel)}
          onWriteOff={() => setShowWriteOff(currentSel)}
          markReadyPending={markReadyMut.isPending}
        />
      ) : tab === 'detail' ? (
        <CipEmptyCard />
      ) : null}
      {tab === 'inbox' && <CipClearingInbox projects={projects} />}
      {tab === 'borrowing' && (
        <CipBorrowing year={year} month={borrowMonth} onMonthChange={setBorrowMonth} />
      )}
      {tab === 'capitalize' && (
        <CipCapitalizeList projects={projects} onCap={(p) => setShowCap(p)} />
      )}
      {tab === 'rollforward' && <CipRollforward year={year} />}

      {showCap && <CapitalizeModal proj={showCap} onClose={() => setShowCap(null)} />}
      {showNew && <NewProjectModal onClose={() => setShowNew(false)} />}
      {showBorrow && (
        <BorrowingRunModal year={year} month={borrowMonth} onClose={() => setShowBorrow(false)} />
      )}
      {showWriteOff && <WriteOffModal proj={showWriteOff} onClose={() => setShowWriteOff(null)} />}
    </div>
  );
}

function CipEmptyCard() {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
        <div className="muted" style={{ fontSize: 15 }}>
          ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
        </div>
      </div>
    </div>
  );
}

// Phase 4b — กล่องรับเข้า (AP). Unconsumed Dr CIP_CLEARING lines from AP bills
// tagged to a CIP project. Each "รับเข้าโครงการ" capitalizes the line (Dr
// CIP_ASSET / Cr CIP_CLEARING + a cost entry). Mirrors the FA_CLEARING inbox.
function CipClearingInbox({ projects }: { projects: readonly CipProject[] }) {
  const companyId = useActiveCompanyId();
  const inboxQuery = useCipClearingInbox(companyId);
  const receiveMut = useReceiveFromClearing(companyId);
  const rows = inboxQuery.data ?? [];

  // The row id that's currently being received (for the per-row busy state), and
  // an inline error string — matches the page's setError/inline-message idiom
  // rather than a global toast.
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // cipProjectId → project (for the voucherNo + name columns).
  const projById = useMemo(() => {
    const m = new Map<string, CipProject>();
    for (const p of projects) m.set(p.apiId, p);
    return m;
  }, [projects]);

  const onReceive = (row: { journalLineId: string; cipProjectId: string }) => {
    if (!companyId) return;
    setError(null);
    setOkMsg(null);
    setBusyLineId(row.journalLineId);
    receiveMut.mutate(
      { cipProjectId: row.cipProjectId, journalLineId: row.journalLineId },
      {
        onSuccess: () => {
          setBusyLineId(null);
          setOkMsg('รับเข้าโครงการสำเร็จ — บันทึกเป็นต้นทุน CIP แล้ว');
        },
        onError: (e: unknown) => {
          setBusyLineId(null);
          setError(e instanceof Error ? e.message : 'รับเข้าโครงการไม่สำเร็จ');
        },
      },
    );
  };

  return (
    <div className="tbl-shell" data-testid="cip-clearing-inbox">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          รายการต้นทุนจากใบสำคัญจ่าย (AP) รอเข้าโครงการ — {rows.length} รายการ
        </span>
        <span className="meta" style={{ marginLeft: 'auto' }}>
          พักไว้ในบัญชี CIP_CLEARING (10190021) จนกดรับเข้าโครงการ
        </span>
      </div>
      {error && (
        <div
          className="filter-row"
          data-testid="cip-inbox-error"
          style={{ color: 'var(--negative)', fontSize: 14 }}
        >
          {error}
        </div>
      )}
      {okMsg && (
        <div
          className="filter-row"
          data-testid="cip-inbox-ok"
          style={{ color: 'var(--positive)', fontSize: 14 }}
        >
          {okMsg}
        </div>
      )}
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 100 }}>วันที่</th>
              <th style={{ width: 150 }}>เลขที่ใบสำคัญ</th>
              <th>ผู้ขาย / ผู้รับเงิน</th>
              <th>โครงการ</th>
              <th className="num">จำนวนเงิน</th>
              <th style={{ width: 150 }}>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {inboxQuery.isPending ? (
              <tr>
                <td colSpan={6} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : inboxQuery.error ? (
              <tr>
                <td
                  colSpan={6}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {inboxQuery.error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ไม่มีรายการต้นทุนจาก AP รอเข้าโครงการ
                </td>
              </tr>
            ) : null}
            {rows.map((row) => {
              const proj = projById.get(row.cipProjectId);
              const busy = busyLineId === row.journalLineId;
              return (
                <tr key={row.journalLineId}>
                  <td>{fmtDate(row.date)}</td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{row.billVoucherNo}</a>
                  </td>
                  <td className="muted">{row.vendorName ?? '—'}</td>
                  <td>
                    {proj ? (
                      <>
                        <b>{proj.id}</b>
                        <div className="meta">{proj.name}</div>
                      </>
                    ) : (
                      <span className="muted">{row.cipProjectId}</span>
                    )}
                  </td>
                  <td className="num tnum">
                    <b>{fmt(Number(row.amount))}</b>
                  </td>
                  <td>
                    <button
                      className="btn sm primary"
                      style={{ height: 28, fontSize: 14 }}
                      disabled={busy || receiveMut.isPending}
                      onClick={() => onReceive(row)}
                    >
                      {busy ? 'กำลังรับเข้า…' : 'รับเข้าโครงการ'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length > 0 && (
              <tr className="total">
                <td colSpan={4}>รวม {rows.length} รายการรอเข้าโครงการ</td>
                <td className="num tnum">
                  <b>{fmt(rows.reduce((s, r) => s + Number(r.amount), 0))}</b>
                </td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CipRegisterProps {
  projects: readonly CipProject[];
  year: string;
  isPending: boolean;
  error: Error | null;
  onPick: (p: CipProject) => void;
  onCap: (p: CipProject) => void;
  onMarkReady: (p: CipProject) => void;
  markReadyPending: boolean;
}

function CipRegister({
  projects,
  year,
  isPending,
  error,
  onPick,
  onCap,
  onMarkReady,
  markReadyPending,
}: CipRegisterProps) {
  const [q, setQ] = useState<string>('');
  const [statusF, setStatusF] = useState<string>('all');
  const [branchF, setBranchF] = useState<string>('all');
  const [staleOnly, setStaleOnly] = useState<boolean>(false);

  const rows = projects.filter((p) => {
    if (statusF !== 'all' && p.status !== statusF) return false;
    if (branchF !== 'all' && p.branch !== branchF) return false;
    if (year !== 'all' && new Date(p.started).getFullYear().toString() !== year) return false;
    if (staleOnly && agingDaysOver(p) === 0) return false;
    if (
      q &&
      !(
        p.id.toLowerCase().includes(q.toLowerCase()) ||
        p.name.toLowerCase().includes(q.toLowerCase()) ||
        p.branch.toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    return true;
  });

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา รหัส / ชื่อโครงการ / สาขา"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage: SEARCH_BG,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">สถานะ: ทั้งหมด</option>
          <option value="active">{CIP_STATUS.active.label}</option>
          <option value="ready">{CIP_STATUS.ready.label}</option>
          <option value="capitalized">{CIP_STATUS.capitalized.label}</option>
          <option value="hold">{CIP_STATUS.hold.label}</option>
        </select>
        <select className="select" value={branchF} onChange={(e) => setBranchF(e.target.value)}>
          <option value="all">สาขา: ทั้งหมด</option>
          {[...new Set(projects.map((p) => p.branch).filter((b) => b && b !== '—'))].map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={staleOnly}
            onChange={(e) => setStaleOnly(e.target.checked)}
          />
          เฉพาะเกินกำหนด (Stale)
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <span className="meta">{rows.length} โครงการ</span>
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 24 }}>
                <input type="checkbox" />
              </th>
              <th style={{ width: 130 }}>Project Code</th>
              <th>ชื่อโครงการ</th>
              <th>สาขา / ที่ตั้ง</th>
              <th>Project Manager</th>
              <th className="num">งบประมาณ</th>
              <th className="num">CIP สะสม (10260002)</th>
              <th style={{ width: 160 }}>ความคืบหน้า</th>
              <th className="num">วันใช้งานสะสม</th>
              <th>คาดเสร็จ</th>
              <th>สถานะ</th>
              <th style={{ width: 140 }}>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={12} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={12}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีข้อมูล — กดปุ่ม &quot;<b>+ สร้าง Project ใหม่</b>&quot;
                  เพื่อสร้างรายการแรก
                </td>
              </tr>
            ) : null}
            {rows.map((p) => {
              const st = getStatus(p.status);
              const barColor =
                p.progress > 95 ? 'var(--positive)' : p.progress > 80 ? 'var(--brand)' : st.tone;
              return (
                <tr key={p.id} onClick={() => onPick(p)} style={{ cursor: 'pointer' }}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" />
                  </td>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{p.id}</a>
                  </td>
                  <td>
                    <b>{p.name}</b>
                    {isOverBudget(p) && (
                      <span
                        className="badge warning"
                        style={{ marginLeft: 8, fontSize: 12 }}
                        title={`ต้นทุน ${fmt(p.total)} เกินงบ ${fmt(p.budget)}`}
                      >
                        เกินงบ
                      </span>
                    )}
                  </td>
                  <td className="muted">{p.branch}</td>
                  <td className="muted">{p.pm}</td>
                  <td className="num tnum muted">{fmt(p.budget)}</td>
                  <td className="num tnum">
                    <b>{fmt(p.total)}</b>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(100, p.progress)}%`,
                            height: '100%',
                            background: barColor,
                          }}
                        />
                      </div>
                      <span className="meta" style={{ minWidth: 40, textAlign: 'right' }}>
                        {p.progress}%
                      </span>
                    </div>
                  </td>
                  <td className="num">{p.daysActive} วัน</td>
                  <td>{fmtDate(p.expected)}</td>
                  <td>
                    <span className={`badge ${st.badge}`}>
                      <span className="bdot" />
                      {st.label}
                    </span>
                    {agingDaysOver(p) > 0 && (
                      <div
                        className="badge"
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          background: '#fce9e7',
                          color: 'var(--negative)',
                        }}
                      >
                        เกินกำหนด {agingDaysOver(p)} วัน
                      </div>
                    )}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {p.status === 'ready' ? (
                      <button
                        className="btn sm primary"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onCap(p)}
                      >
                        โอนเป็น FA →
                      </button>
                    ) : p.status === 'active' && p.total > 0 ? (
                      <button
                        className="btn sm"
                        style={{ height: 28, fontSize: 14 }}
                        disabled={markReadyPending}
                        onClick={() => onMarkReady(p)}
                      >
                        พร้อมใช้งาน
                      </button>
                    ) : (
                      <button
                        className="btn sm ghost"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onPick(p)}
                      >
                        ดูต้นทุน
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td colSpan={5}>รวม {rows.length} โครงการ</td>
              <td className="num tnum">{fmt(rows.reduce((s, p) => s + p.budget, 0))}</td>
              <td className="num tnum">{fmt(rows.reduce((s, p) => s + p.total, 0))}</td>
              <td colSpan={5} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface CipDetailProps {
  proj: CipProject;
  projects: readonly CipProject[];
  setProj: (p: CipProject) => void;
  onCap: () => void;
  onMarkReady: () => void;
  onWriteOff: () => void;
  markReadyPending: boolean;
}

function CipDetail({
  proj,
  projects,
  setProj,
  onCap,
  onMarkReady,
  onWriteOff,
  markReadyPending,
}: CipDetailProps) {
  const companyId = useActiveCompanyId();
  const entriesQuery = useCipCostEntries(companyId, proj.apiId);
  const retentionQuery = useCipRetention(companyId, proj.apiId);
  const retention = retentionQuery.data?.summary;
  const items: CostItem[] = (entriesQuery.data ?? []).map((e: CipCostEntry) => ({
    date: new Date(e.entryDate).toISOString().slice(0, 10),
    ref: e.sourceRef ?? '—',
    desc: e.description,
    vendor: e.vendorName ?? '—',
    pool: e.pool,
    amt: Number(e.amount),
  }));
  const [showAdd, setShowAdd] = useState(false);
  const [showRelease, setShowRelease] = useState(false);
  const [showSuspend, setShowSuspend] = useState(false);
  const st = getStatus(proj.status);
  // Phase 5 (IAS 23.20) — only a genuinely SUSPENDED project (not a CANCELLED one,
  // which also maps to 'hold') can be resumed; an ACTIVE one can be suspended.
  const isSuspended = proj.apiStatus === 'SUSPENDED';
  const resumeMut = useResumeCipProject(companyId);
  const onResume = () => {
    if (!proj.apiId || !companyId) return;
    resumeMut.mutate({
      cipProjectId: proj.apiId,
      data: { resumeDate: new Date().toISOString().slice(0, 10) },
    });
  };

  const exportItems = () => {
    const cols: ExportColumn<CostItem>[] = [
      { header: 'วันที่', value: (it) => it.date, width: 12 },
      { header: 'อ้างอิง', value: (it) => it.ref, width: 18 },
      { header: 'รายการ', value: (it) => it.desc, width: 40 },
      { header: 'ผู้ขาย', value: (it) => it.vendor, width: 24 },
      { header: 'กอง', value: (it) => getPool(it.pool).label, width: 28 },
      { header: 'จำนวนเงิน', value: (it) => it.amt, width: 16 },
    ];
    void exportRows(`CIP_${proj.id}_${todayStamp()}.xlsx`, proj.id, cols, items);
  };

  const poolBreakdown = COST_POOLS.map((c) => {
    const amt = proj.costs[c.code] ?? 0;
    return {
      ...c,
      amt,
      pct: proj.total ? (amt / proj.total) * 100 : 0,
      count: items.filter((i) => i.pool === c.code).length,
    };
  });

  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
            <select
              className="select"
              style={{ width: 480, height: 38, fontSize: 14 }}
              value={proj.id}
              onChange={(e) => {
                const next = projects.find((p) => p.id === e.target.value);
                if (next) setProj(next);
              }}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.id} — {p.name}
                </option>
              ))}
            </select>
            <span className={`badge ${st.badge}`}>
              <span className="bdot" />
              {st.label}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn sm" onClick={exportItems}>
              <Icon name="download" size={12} /> Export
            </button>
            {proj.status === 'active' && proj.total > 0 && (
              <button className="btn sm" onClick={onMarkReady} disabled={markReadyPending}>
                <Icon name="check" size={12} /> ทำเครื่องหมายพร้อมใช้งาน
              </button>
            )}
            {proj.status === 'active' && (
              <button className="btn sm" onClick={() => setShowSuspend(true)}>
                <Icon name="x" size={12} /> ระงับชั่วคราว
              </button>
            )}
            {isSuspended && (
              <button className="btn sm primary" onClick={onResume} disabled={resumeMut.isPending}>
                <Icon name="refresh" size={12} /> กลับมาทำต่อ
              </button>
            )}
            {(proj.status === 'active' || proj.status === 'ready') && (
              <button className="btn sm danger" onClick={onWriteOff}>
                <Icon name="x" size={12} /> ตัดจำหน่าย (Write-off)
              </button>
            )}
            {(proj.status === 'active' || proj.status === 'ready') && (
              <button className="btn sm primary" onClick={onCap}>
                <Icon name="check" size={12} /> โอนเป็นทรัพย์สิน
              </button>
            )}
          </div>
        </div>

        {isSuspended && (
          <div
            style={{
              padding: '12px 18px',
              background: '#fff7ed',
              borderTop: '1px solid var(--border)',
              fontSize: 14,
              lineHeight: 1.55,
              color: '#b54708',
            }}
            data-testid="cip-suspend-note"
          >
            <b>ระงับชั่วคราว (IAS 23.20)</b> — หยุดรวมดอกเบี้ยเป็นทุนระหว่างที่หยุดพัฒนา
            {proj.suspendReason ? (
              <>
                {' '}
                · เหตุผล: <b>{proj.suspendReason}</b>
              </>
            ) : null}
          </div>
        )}

        <div
          style={{
            padding: '16px 18px',
            background: '#fafafa',
            borderTop: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 16 }}>
            <div>
              <div className="meta">สาขา / ที่ตั้ง</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{proj.branch}</div>
              <div className="meta">PM: {proj.pm}</div>
            </div>
            <div>
              <div className="meta">งบประมาณ</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(proj.budget)}
              </div>
            </div>
            <div>
              <div className="meta">CIP สะสมจริง</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(proj.total)}
              </div>
            </div>
            <div>
              <div className="meta">งบคงเหลือ</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  color: proj.budget - proj.total >= 0 ? 'var(--positive)' : 'var(--negative)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(proj.budget - proj.total)}
              </div>
            </div>
            <div>
              <div className="meta">ระยะเวลา</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>
                {fmtDate(proj.started)}
              </div>
              <div className="meta">→ {fmtDate(proj.expected)}</div>
            </div>
            <div>
              <div className="meta">ความคืบหน้า</div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {proj.progress}%
              </div>
              <div className="meta">{proj.daysActive} วันใช้งาน</div>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 18px' }}>
          <div className="section-title" style={{ margin: '0 0 10px' }}>
            Cost Pool Breakdown
          </div>
          <div
            style={{
              display: 'flex',
              height: 14,
              borderRadius: 7,
              overflow: 'hidden',
              background: '#f0f0f3',
              marginBottom: 12,
            }}
          >
            {poolBreakdown.map(
              (c) =>
                c.amt > 0 && (
                  <div
                    key={c.code}
                    style={{ width: `${c.pct}%`, background: c.tone }}
                    title={`${c.label}: ${fmt(c.amt)}`}
                  />
                ),
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14 }}>
            {poolBreakdown.map((c) => (
              <div
                key={c.code}
                style={{
                  padding: '10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  borderLeft: `3px solid ${c.tone}`,
                }}
              >
                <div className="meta">{c.label}</div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(c.amt)}
                </div>
                <div className="meta">
                  {c.pct.toFixed(1)}% · {c.count} รายการ
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Phase 4 — contractor retention (เงินประกันผลงาน) tracked off-CIP. */}
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>เงินประกันผลงาน (Retention)</h3>
          <button
            className="btn sm"
            onClick={() => setShowRelease(true)}
            disabled={Number(retention?.outstanding ?? 0) <= 0}
          >
            <Icon name="check" size={12} /> คืนเงินประกัน
          </button>
        </div>
        <div style={{ padding: '16px 18px' }}>
          {retentionQuery.isPending ? (
            <div className="muted">กำลังโหลด…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
              <div>
                <div className="meta">หักไว้สะสม (Withheld)</div>
                <div
                  className="tnum"
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {fmt(Number(retention?.withheld ?? 0))}
                </div>
              </div>
              <div>
                <div className="meta">คืนแล้ว (Released)</div>
                <div
                  className="tnum"
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {fmt(Number(retention?.released ?? 0))}
                </div>
              </div>
              <div>
                <div className="meta">คงค้าง (Outstanding)</div>
                <div
                  className="tnum"
                  style={{
                    fontSize: 18,
                    fontWeight: 600,
                    marginTop: 4,
                    color: 'var(--brand)',
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {fmt(Number(retention?.outstanding ?? 0))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            รายการประกอบ CIP — Open Items {items.length} รายการ
          </span>
          <select className="select">
            <option>Cost Pool: ทั้งหมด</option>
            {COST_POOLS.map((c) => (
              <option key={c.code}>{c.label}</option>
            ))}
          </select>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="btn sm primary" onClick={() => setShowAdd(true)}>
              <Icon name="plus" size={12} /> เพิ่มต้นทุน
            </button>
            <button className="btn sm" onClick={exportItems}>
              <Icon name="download" size={12} /> Export Excel
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 520 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 60 }}>ลำดับ</th>
                <th style={{ width: 100 }}>วันที่</th>
                <th style={{ width: 140 }}>JV/Ref</th>
                <th>รายการ</th>
                <th>Vendor</th>
                <th>Cost Pool</th>
                <th className="num">จำนวนเงิน</th>
              </tr>
            </thead>
            <tbody>
              {entriesQuery.isPending ? (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    ยังไม่มีรายการต้นทุนในโครงการนี้
                  </td>
                </tr>
              ) : null}
              {items.map((it, idx) => {
                const p = getPool(it.pool);
                return (
                  <tr key={`${it.ref}-${idx}`}>
                    <td className="num">{idx + 1}</td>
                    <td>{fmtDate(it.date)}</td>
                    <td className="code">
                      <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{it.ref}</a>
                    </td>
                    <td>{it.desc}</td>
                    <td className="muted">{it.vendor}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                        <span
                          style={{ width: 9, height: 9, borderRadius: 2, background: p.tone }}
                        />
                        {p.label}
                      </span>
                    </td>
                    <td className="num tnum">{fmt(it.amt)}</td>
                  </tr>
                );
              })}
              <tr className="total">
                <td colSpan={6}>รวมต้นทุนสะสมในโครงการ {proj.id}</td>
                <td className="num tnum">
                  <b>{fmt(proj.total)}</b>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>ยอดสะสมในตารางนี้ตรงกับยอดในบัญชี 10260002 — งานระหว่างก่อสร้าง ({proj.id})</span>
        </div>
      </div>
      {showAdd && <AddCostEntryModal proj={proj} onClose={() => setShowAdd(false)} />}
      {showRelease && (
        <ReleaseRetentionModal
          proj={proj}
          outstanding={Number(retention?.outstanding ?? 0)}
          onClose={() => setShowRelease(false)}
        />
      )}
      {showSuspend && <SuspendModal proj={proj} onClose={() => setShowSuspend(false)} />}
    </>
  );
}

// Borrowing-cost tab — TFRS 23 weighted cap-rate + per-project allocation,
// driven by the loans master (useLoans), computeCapRate (useCipCapRate) and the
// monthly preview (useCipBorrowingPreview) for the selected period.
interface CipBorrowingProps {
  year: string;
  month: string;
  onMonthChange: (month: string) => void;
}

function CipBorrowing({ year, month, onMonthChange }: CipBorrowingProps) {
  const companyId = useActiveCompanyId();
  const period = `${year}${month}`;
  const loansQuery = useLoans(companyId, true);
  const capRateQuery = useCipCapRate(companyId, period);
  const previewQuery = useCipBorrowingPreview(companyId, period);
  const runsQuery = useCipBorrowingRuns(companyId, period);
  // Phase 5 (IAS 23.26) — annual disclosure of capitalized borrowing cost +
  // the cap rate(s) used, aggregated from the year's posted CB runs.
  const disclosureQuery = useCipBorrowingDisclosure(companyId, year);

  // The live run for the period (CANCELLED runs are re-submittable, so they don't
  // count as "current"). Drives the run-status banner above the allocation table.
  const currentRun = (runsQuery.data ?? []).find((r) => r.status !== 'CANCELLED');
  const runBadge = currentRun
    ? cipApprovalBadge(currentRun.approvalStatus, currentRun.status)
    : null;

  const loans = loansQuery.data ?? [];
  const cap = capRateQuery.data;
  const capRate = Number(cap?.capRate ?? 0);
  const previewRows = previewQuery.data ?? [];

  const generalLoans = loans.filter((l) => l.purpose === 'GENERAL');
  const totalGen = generalLoans.reduce((s, l) => s + Number(l.principal), 0);
  const totalGenInterest = generalLoans.reduce(
    (s, l) => s + Number(l.principal) * Number(l.rate),
    0,
  );
  const monthLabel = `${thaiMonthLabel(month)} ${year}`;
  const totalAlloc = previewRows.reduce((s, r) => s + Number(r.capitalizable), 0);

  // IAS 23.26 disclosure rows (period / cap rate% / amount) for the export + table.
  const disclosure = disclosureQuery.data;
  const disclosureRows = disclosure?.capRates ?? [];
  const disclosureTotal = Number(disclosure?.totalCapitalized ?? 0);
  const exportDisclosure = () => {
    type DiscRow = (typeof disclosureRows)[number];
    const cols: ExportColumn<DiscRow>[] = [
      { header: 'งวด', value: (r) => r.period, width: 14 },
      { header: 'Capitalization Rate (%)', value: (r) => Number(r.capRate), width: 22 },
      { header: 'ดอกเบี้ยที่รวมเป็นทุน', value: (r) => Number(r.amount), width: 22 },
    ];
    void exportRows(
      `CIP_BorrowingDisclosure_${year}_${todayStamp()}.xlsx`,
      `IAS 23.26 ${year}`,
      cols,
      disclosureRows,
    );
  };

  return (
    <>
      <div className="grid-2-eq" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card">
          <div className="card-head">
            <h3>เงินกู้ของบริษัท</h3>
            <span className="meta">ใช้คำนวณ Capitalization Rate</span>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>เลขที่</th>
                  <th>ธนาคาร / สัญญา</th>
                  <th className="num">เงินต้น</th>
                  <th className="num">ดอกเบี้ย/ปี</th>
                  <th>วัตถุประสงค์</th>
                </tr>
              </thead>
              <tbody>
                {loansQuery.isPending ? (
                  <tr>
                    <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                      กำลังโหลด…
                    </td>
                  </tr>
                ) : loans.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                      ยังไม่มีข้อมูลเงินกู้
                    </td>
                  </tr>
                ) : null}
                {loans.map((l) => (
                  <tr key={l.id}>
                    <td className="code">{l.code}</td>
                    <td>{l.bank}</td>
                    <td className="num tnum">{fmt(Number(l.principal))}</td>
                    <td className="num">
                      <b>{Number(l.rate).toFixed(2)}%</b>
                    </td>
                    <td>
                      {l.purpose === 'GENERAL' ? (
                        <span className="badge info" style={{ fontSize: 13 }}>
                          General
                        </span>
                      ) : (
                        <span className="badge brand" style={{ fontSize: 13 }}>
                          {l.purpose}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="total">
                  <td colSpan={2}>รวม General Loan (ใช้คำนวณ Cap Rate)</td>
                  <td className="num tnum">{fmt(totalGen)}</td>
                  <td className="num">
                    <b>{capRate.toFixed(2)}%</b>
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h3>Capitalization Rate — เดือน {monthLabel}</h3>
          </div>
          <div className="card-body">
            <div style={{ padding: '16px', background: '#fafafa', borderRadius: 8 }}>
              <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 4 }}>
                สูตร: Weighted Average Rate ของเงินกู้ General
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontFamily: 'var(--font-mono)',
                  marginBottom: 14,
                  padding: '8px 10px',
                  background: 'white',
                  borderRadius: 4,
                }}
              >
                Σ(Principal × Rate) ÷ Σ(Principal) = {fmt(totalGenInterest / 100 / 1_000_000, 1)}M ÷{' '}
                {fmt(totalGen / 1_000_000, 0)}M
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span
                  style={{
                    fontSize: 36,
                    fontWeight: 700,
                    color: 'var(--brand)',
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {capRate.toFixed(2)}%
                </span>
                <span className="meta">ต่อปี = {(capRate / 12).toFixed(4)}% ต่อเดือน</span>
              </div>
            </div>
            <div
              style={{
                marginTop: 14,
                padding: '12px 14px',
                background: '#e8eff8',
                borderRadius: 6,
                fontSize: 13.5,
                lineHeight: 1.55,
              }}
            >
              <b>TFRS 23 — ข้อสำคัญ:</b>
              <br />
              • Specific borrowing → ใช้อัตราของเงินกู้นั้นโดยตรง
              <br />
              • General borrowing → ใช้ Weighted Average Rate (วิธี Capitalize)
              <br />
              • Capitalize เฉพาะช่วงที่ Active Construction (ไม่นับช่วงระงับ)
              <br />• ไม่เกินดอกเบี้ยที่จ่ายจริงในงวดนั้น
            </div>
          </div>
        </div>
      </div>

      {/* Phase 3b — current period's borrowing-run status (DRAFT/PENDING/POSTED)
          + chain summary, so the tab reflects 4-eyes gating, not a silent run. */}
      {currentRun && runBadge && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '12px 16px',
            marginBottom: 'var(--section-gap)',
            background: '#fafafa',
            border: '1px solid var(--border)',
            borderRadius: 8,
          }}
          data-testid="cip-run-status-banner"
        >
          <span className={`badge ${runBadge.tone}`}>
            <span className="bdot" />
            {runBadge.label}
          </span>
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            รอบประมวลผลดอกเบี้ย {currentRun.voucherNo}
          </span>
          <span className="meta">งวด {monthLabel}</span>
          {currentRun.submittedBy && (
            <span className="meta">· ส่งอนุมัติโดย {currentRun.submittedBy}</span>
          )}
          {currentRun.approvalStatus === 'PENDING' && (
            <span className="meta">
              · รออนุมัติขั้น {currentRun.currentStep}/{currentRun.approvals?.length ?? 0}
            </span>
          )}
        </div>
      )}

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            การปันส่วนดอกเบี้ยรายโครงการ — เดือน {monthLabel}
          </span>
          <select className="select" value={month} onChange={(e) => onMonthChange(e.target.value)}>
            {THAI_MONTHS.map(([v, l]) => (
              <option key={v} value={v}>
                งวด: {l} {year}
              </option>
            ))}
          </select>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Project</th>
              <th>เงินกู้อ้างอิง</th>
              <th className="num">CIP เฉลี่ยเดือนนี้</th>
              <th className="num">Capitalization Rate</th>
              <th className="num">ดอกเบี้ยที่ Capitalize</th>
              <th>บัญชี Cr.</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {previewQuery.isPending ? (
              <tr>
                <td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : previewRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  ไม่มีดอกเบี้ยที่ต้อง Capitalize ในงวดนี้
                </td>
              </tr>
            ) : null}
            {previewRows.map((r) => (
              <tr key={r.cipProjectId}>
                <td>
                  <b>{r.voucherNo}</b>
                  <div className="meta">{r.name}</div>
                </td>
                <td className="muted">{r.loanRef}</td>
                <td className="num tnum">{fmt(Number(r.avgCipBase), 0)}</td>
                <td className="num">
                  <b>{Number(r.rateApplied).toFixed(2)}%</b>
                </td>
                <td className="num tnum" style={{ color: '#7030A0' }}>
                  <b>{fmt(Number(r.capitalizable), 0)}</b>
                </td>
                <td className="code muted">60200004 ดอกเบี้ยจ่าย</td>
                <td>
                  <span className="badge warning">
                    <span className="bdot" />
                    รอ Post
                  </span>
                </td>
              </tr>
            ))}
            <tr className="total">
              <td colSpan={4}>รวมดอกเบี้ยที่จะ Capitalize เดือน {monthLabel}</td>
              <td className="num tnum" style={{ color: '#7030A0' }}>
                <b>{fmt(totalAlloc, 0)}</b>
              </td>
              <td colSpan={2} />
            </tr>
          </tbody>
        </table>
        <div
          style={{
            padding: '14px 18px',
            background: '#fafafa',
            fontSize: 14,
            color: 'var(--text-muted)',
            lineHeight: 1.55,
            borderTop: '1px solid var(--border)',
          }}
        >
          ระบบจะสร้าง JV:{' '}
          <b className="code">Dr. 10260002 CIP (แยกตาม Project) / Cr. 60200004 ดอกเบี้ยจ่าย</b>{' '}
          เพื่อรับรู้ ดอกเบี้ยกลับเข้า CIP — JV จะถูกบันทึกเมื่ออนุมัติครบทุกขั้น (4-eyes)
        </div>
      </div>

      {/* Phase 5 (IAS 23.26) — annual disclosure: total borrowing cost
          capitalized in the period + the capitalization rate(s) used. */}
      <div
        className="card"
        style={{ marginTop: 'var(--section-gap)' }}
        data-testid="cip-disclosure"
      >
        <div className="card-head">
          <h3>IAS 23.26 — การเปิดเผยข้อมูลดอกเบี้ยที่รวมเป็นทุน</h3>
          <button
            className="btn sm"
            onClick={exportDisclosure}
            disabled={disclosureRows.length === 0}
          >
            <Icon name="download" size={12} /> Export Excel
          </button>
        </div>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-end' }}>
            <div>
              <div className="meta">ดอกเบี้ยที่รวมเป็นทุนทั้งปี {year} (23.26(a))</div>
              <div
                className="tnum"
                style={{
                  fontSize: 36,
                  fontWeight: 700,
                  color: '#7030A0',
                  fontFamily: 'var(--font-display)',
                  lineHeight: 1.1,
                  marginTop: 4,
                }}
              >
                {fmt(disclosureTotal)} <span className="unit">บาท</span>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <div className="meta" style={{ marginBottom: 8 }}>
              อัตราการรวมเป็นทุน (Capitalization Rate) รายงวด — 23.26(b)
            </div>
            <table className="tbl">
              <thead>
                <tr>
                  <th>งวด</th>
                  <th className="num">Capitalization Rate</th>
                  <th className="num">ดอกเบี้ยที่รวมเป็นทุน</th>
                </tr>
              </thead>
              <tbody>
                {disclosureQuery.isPending ? (
                  <tr>
                    <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                      กำลังโหลด…
                    </td>
                  </tr>
                ) : disclosureRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                      ยังไม่มีดอกเบี้ยที่รวมเป็นทุนในปี {year}
                    </td>
                  </tr>
                ) : null}
                {disclosureRows.map((r) => (
                  <tr key={r.period}>
                    <td className="code">{r.period}</td>
                    <td className="num">
                      <b>{Number(r.capRate).toFixed(4)}%</b>
                    </td>
                    <td className="num tnum" style={{ color: '#7030A0' }}>
                      <b>{fmt(Number(r.amount))}</b>
                    </td>
                  </tr>
                ))}
                {disclosureRows.length > 0 && (
                  <tr className="total">
                    <td colSpan={2}>รวมทั้งปี {year}</td>
                    <td className="num tnum" style={{ color: '#7030A0' }}>
                      <b>{fmt(disclosureTotal)}</b>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

// CIP rollforward — month-end movement (opening + additions − capitalizedOut −
// writtenOff = closing). Period is derived from the page-header year (defaults
// to mid-year June so a full set of months has data).
function CipRollforward({ year }: { year: string }) {
  const companyId = useActiveCompanyId();
  const [month, setMonth] = useState<string>('06');
  const period = `${year}${month}`;
  const rfQuery = useCipRollforward(companyId, period);
  const rows: readonly CipRollforwardRow[] = rfQuery.data ?? [];

  const totals = rows.reduce(
    (acc, r) => ({
      opening: acc.opening + Number(r.opening),
      additions: acc.additions + Number(r.additions),
      capitalizedOut: acc.capitalizedOut + Number(r.capitalizedOut),
      writtenOff: acc.writtenOff + Number(r.writtenOff),
      closing: acc.closing + Number(r.closing),
    }),
    { opening: 0, additions: 0, capitalizedOut: 0, writtenOff: 0, closing: 0 },
  );

  const exportRf = () => {
    const cols: ExportColumn<CipRollforwardRow>[] = [
      { header: 'รหัส', value: (r) => r.voucherNo, width: 16 },
      { header: 'ชื่อโครงการ', value: (r) => r.name, width: 34 },
      { header: 'สาขา', value: (r) => r.branchName ?? '—', width: 22 },
      { header: 'ยอดยกมา', value: (r) => Number(r.opening), width: 16 },
      { header: 'เพิ่มระหว่างงวด', value: (r) => Number(r.additions), width: 16 },
      { header: 'โอนออก', value: (r) => Number(r.capitalizedOut), width: 16 },
      { header: 'ตัดจำหน่าย', value: (r) => Number(r.writtenOff), width: 16 },
      { header: 'ยอดคงเหลือ', value: (r) => Number(r.closing), width: 16 },
    ];
    void exportRows(
      `CIP_Rollforward_${period}_${todayStamp()}.xlsx`,
      'CIP Rollforward',
      cols,
      rows,
    );
  };

  const months: readonly [string, string][] = [
    ['01', 'ม.ค.'],
    ['02', 'ก.พ.'],
    ['03', 'มี.ค.'],
    ['04', 'เม.ย.'],
    ['05', 'พ.ค.'],
    ['06', 'มิ.ย.'],
    ['07', 'ก.ค.'],
    ['08', 'ส.ค.'],
    ['09', 'ก.ย.'],
    ['10', 'ต.ค.'],
    ['11', 'พ.ย.'],
    ['12', 'ธ.ค.'],
  ];

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          CIP Rollforward — สรุปยกยอดงวด {month}/{year}
        </span>
        <select className="select" value={month} onChange={(e) => setMonth(e.target.value)}>
          {months.map(([v, l]) => (
            <option key={v} value={v}>
              งวด: {l} {year}
            </option>
          ))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn sm" onClick={exportRf}>
            <Icon name="download" size={12} /> Export Excel
          </button>
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 130 }}>Project Code</th>
              <th>ชื่อโครงการ</th>
              <th>สาขา</th>
              <th className="num">ยอดยกมา</th>
              <th className="num">เพิ่มระหว่างงวด</th>
              <th className="num">โอนออก (Capitalized)</th>
              <th className="num">ตัดจำหน่าย</th>
              <th className="num">ยอดคงเหลือ</th>
            </tr>
          </thead>
          <tbody>
            {rfQuery.isPending ? (
              <tr>
                <td colSpan={8} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : rfQuery.error ? (
              <tr>
                <td
                  colSpan={8}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {rfQuery.error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ไม่มีความเคลื่อนไหวในงวดนี้
                </td>
              </tr>
            ) : null}
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="code">
                  <b>{r.voucherNo}</b>
                </td>
                <td>{r.name}</td>
                <td className="muted">{r.branchName ?? '—'}</td>
                <td className="num tnum">{fmt(Number(r.opening))}</td>
                <td className="num tnum">{fmt(Number(r.additions))}</td>
                <td className="num tnum">{fmt(Number(r.capitalizedOut))}</td>
                <td className="num tnum">{fmt(Number(r.writtenOff))}</td>
                <td className="num tnum">
                  <b>{fmt(Number(r.closing))}</b>
                </td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="total">
                <td colSpan={3}>รวม {rows.length} โครงการ</td>
                <td className="num tnum">{fmt(totals.opening)}</td>
                <td className="num tnum">{fmt(totals.additions)}</td>
                <td className="num tnum">{fmt(totals.capitalizedOut)}</td>
                <td className="num tnum">{fmt(totals.writtenOff)}</td>
                <td className="num tnum">
                  <b>{fmt(totals.closing)}</b>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="tbl-footer">
        <span>ยอดคงเหลือ = ยอดยกมา + เพิ่มระหว่างงวด − โอนออก − ตัดจำหน่าย (บัญชี 10260002)</span>
      </div>
    </div>
  );
}

interface CipCapListProps {
  projects: readonly CipProject[];
  onCap: (p: CipProject) => void;
}

function CipCapitalizeList({ projects, onCap }: CipCapListProps) {
  const ready = projects.filter(
    (p) => p.status === 'ready' || (p.status === 'active' && p.total > 0),
  );
  if (ready.length === 0) {
    return (
      <div className="card">
        <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 15 }}>
            ยังไม่มีโครงการที่พร้อมโอนเป็นทรัพย์สิน
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          โครงการที่พร้อมโอนเป็นทรัพย์สิน หรือใกล้พร้อม
        </span>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>Project</th>
            <th>ชื่อ</th>
            <th>สาขา</th>
            <th className="num">ต้นทุนรวม</th>
            <th className="num">ความคืบหน้า</th>
            <th>คาดเสร็จ</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {ready.map((p) => {
            const st = getStatus(p.status);
            return (
              <tr key={p.id}>
                <td className="code">
                  <b>{p.id}</b>
                </td>
                <td>{p.name}</td>
                <td className="muted">{p.branch}</td>
                <td className="num tnum">
                  <b>{fmt(p.total)}</b>
                </td>
                <td className="num">{p.progress}%</td>
                <td>{fmtDate(p.expected)}</td>
                <td>
                  <span className={`badge ${st.badge}`}>
                    <span className="bdot" />
                    {st.label}
                  </span>
                </td>
                <td>
                  <button
                    className="btn sm primary"
                    style={{ height: 28, fontSize: 14 }}
                    onClick={() => onCap(p)}
                  >
                    เริ่มโอน →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface CapitalizeModalProps {
  proj: CipProject;
  onClose: () => void;
}

function CapitalizeModal({ proj, onClose }: CapitalizeModalProps) {
  const companyId = useActiveCompanyId();
  const capMut = useCapitalizeCipProject(companyId);
  const requestsQuery = useCipCapitalizeRequests(companyId, proj.apiId || undefined);
  const approveMut = useApproveCipCapitalizeRequest(companyId);
  const rejectMut = useRejectCipCapitalizeRequest(companyId);
  const returnMut = useReturnCipCapitalizeRequest(companyId);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [capError, setCapError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showDecide, setShowDecide] = useState(false);

  // An in-flight capitalize request for this project (DRAFT/PENDING/RETURNED) —
  // CAPITALIZED/CANCELLED are terminal and don't block a re-submit. While one
  // exists the wizard's confirm step flips to the approval-chain view.
  const openRequest = (requestsQuery.data ?? []).find(
    (r) => r.status !== 'CAPITALIZED' && r.status !== 'CANCELLED',
  );
  const reqPending = openRequest?.approvalStatus === 'PENDING';
  const reqStep = openRequest?.currentStep ?? 0;
  const reqTotalSteps = openRequest?.approvals?.length ?? 0;
  const reqBadge = openRequest
    ? cipApprovalBadge(openRequest.approvalStatus, openRequest.status)
    : null;
  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;

  // If an open capitalize request already exists (or one was just submitted),
  // pin the view to the confirm step so the approval chain is the focus instead
  // of re-walking the cost/split wizard. Derived (no effect) — the wizard's own
  // `step` state still drives navigation in the non-submitted path.
  const inApproval = !!openRequest || submitted;
  const viewStep: 1 | 2 | 3 = inApproval ? 3 : step;

  // Prospective posting date for an as-yet-unsubmitted request — the live current
  // date (injectable clock keeps tests deterministic). The submit posts THIS date
  // so the step-3 header preview matches what the server records.
  const capitalDate = cipRefNow().toISOString().slice(0, 10);

  const submitCapitalize = () => {
    if (!proj.apiId || !companyId) {
      onClose();
      return;
    }
    setCapError(null);
    // Only attach the multi-asset splits when the balance check passes (Σ splits
    // === CIP total). Otherwise block — the server re-validates the sum anyway.
    const balanced = Math.abs(diff) < 1;
    const splitPayload =
      balanced && positiveSplits.length > 0
        ? positiveSplits.map((s) => ({
            name: s.name,
            categoryCode: catToCode(s.cat),
            usefulLifeMonths: s.life * 12,
            amount: Math.round(s.amt),
          }))
        : undefined;
    if (positiveSplits.length > 0 && !balanced) {
      setCapError('ยอดที่แบ่งไม่ตรงกับต้นทุน CIP — กรุณาปรับให้ผลต่างเป็น 0 ก่อนยืนยัน');
      return;
    }
    capMut.mutate(
      {
        cipProjectId: proj.apiId,
        data: {
          capitalDate,
          ...(splitPayload ? { splits: splitPayload } : {}),
        },
      },
      {
        // Phase 3b — the endpoint now SUBMITS a capitalize request for approval
        // (it no longer posts immediately). Keep the modal open and surface the
        // pending-approval chain (the requests query re-fetches via invalidation).
        onSuccess: () => setSubmitted(true),
        onError: (e: unknown) =>
          setCapError(e instanceof Error ? e.message : 'ส่งคำขออนุมัติไม่สำเร็จ'),
      },
    );
  };

  const handleDecide = (d: Decision) => {
    if (!openRequest || !proj.apiId) return;
    setCapError(null);
    const onError = (e: unknown) =>
      setCapError(e instanceof Error ? e.message : 'ดำเนินการไม่สำเร็จ');
    const onSuccess = () => setShowDecide(false);
    const base = { cipProjectId: proj.apiId, reqId: openRequest.id };
    if (d.action === 'approve') {
      approveMut.mutate(
        {
          ...base,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        },
        { onSuccess, onError },
      );
    } else if (d.action === 'return') {
      returnMut.mutate(
        {
          ...base,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        },
        { onSuccess, onError },
      );
    } else {
      rejectMut.mutate(
        {
          ...base,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        },
        { onSuccess, onError },
      );
    }
  };
  const borrow = proj.costs.BORROW ?? 0;
  const other = proj.costs.OTHER ?? 0;
  const indirect = proj.costs.INDIRECT ?? 0;
  const initialSplits: FaSplit[] = [
    {
      name: `${proj.name} — เครื่องจักรหลัก`,
      cat: 'เครื่องจักร',
      life: 15,
      amt: (proj.costs.DIRECT_EQ ?? 0) + borrow * 0.78 + other * 0.6,
    },
    {
      name: `${proj.name} — อาคารและงานก่อสร้าง`,
      cat: 'อาคารและสิ่งปลูกสร้าง',
      life: 25,
      amt: (proj.costs.DIRECT_CONS ?? 0) + borrow * 0.18 + other * 0.35,
    },
    {
      name: `${proj.name} — งานระบบเสริม`,
      cat: 'เครื่องจักร',
      life: 10,
      amt: indirect + borrow * 0.04 + other * 0.05,
    },
  ];
  const [splits, setSplits] = useState<FaSplit[]>(initialSplits);

  const totalSplits = splits.reduce((s, x) => s + x.amt, 0);
  const diff = proj.total - totalSplits;
  const stepDef: readonly { n: 1 | 2 | 3; t: string }[] = [
    { n: 1, t: 'ทบทวนต้นทุน' },
    { n: 2, t: 'แบ่งเป็นทรัพย์สิน' },
    { n: 3, t: 'ยืนยันและสร้าง JV' },
  ];

  const positiveSplits = splits.filter((s) => s.amt > 0);

  // Step-3 voucher header (FIX: was a hardcoded JV no. + literal date). The
  // voucher is server-assigned, so show it only once a request exists; before
  // submit it's a placeholder. The posting date is the request's capitalDate
  // (= the date we submit), rendered via fmtDate — never a literal.
  const headerVoucherNo = openRequest?.voucherNo ?? 'จะกำหนดอัตโนมัติ';
  const headerCapitalDate = openRequest?.capitalDate ?? capitalDate;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 1000,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              โอนงานระหว่างก่อสร้างเป็นทรัพย์สิน — {proj.id}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {proj.name} · ต้นทุนรวม <b style={{ color: 'var(--brand)' }}>{fmt(proj.total)}</b> บาท
              · {proj.branch}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderBottom: '1px solid var(--border)',
            background: '#fafafa',
            display: 'flex',
            gap: 8,
          }}
        >
          {stepDef.map((s) => (
            <div
              key={s.n}
              style={{
                flex: 1,
                padding: '10px 14px',
                background: viewStep === s.n ? 'var(--brand-tint)' : 'white',
                border: `1px solid ${viewStep === s.n ? 'var(--brand)' : 'var(--border)'}`,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: inApproval ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (!inApproval) setStep(s.n);
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: viewStep >= s.n ? 'var(--brand)' : '#e5e5e9',
                  color: viewStep >= s.n ? 'white' : '#52525b',
                  display: 'grid',
                  placeItems: 'center',
                  fontWeight: 700,
                  fontSize: 14,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {s.n}
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: viewStep === s.n ? 'var(--brand)' : 'inherit',
                }}
              >
                {s.t}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: '18px 22px', overflow: 'auto', flex: 1 }}>
          {viewStep === 1 && (
            <>
              <div className="section-title" style={{ margin: '0 0 10px' }}>
                ขั้นตอนที่ 1 — ทบทวนต้นทุนสะสมในโครงการ
              </div>
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  marginBottom: 14,
                }}
              >
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Cost Pool</th>
                      <th>คำอธิบาย</th>
                      <th className="num">ยอดสะสม</th>
                      <th>นำเข้าทรัพย์สิน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COST_POOLS.map((c) => {
                      const amt = proj.costs[c.code] ?? 0;
                      const desc =
                        c.code === 'BORROW'
                          ? 'ดอกเบี้ยที่ Capitalize ตาม TFRS 23'
                          : c.code === 'DIRECT_EQ'
                            ? 'เครื่องจักร / อุปกรณ์'
                            : c.code === 'DIRECT_CONS'
                              ? 'วัสดุก่อสร้าง / ค่าจ้างผู้รับเหมา'
                              : c.code === 'OTHER'
                                ? 'ค่าธรรมเนียม ทดสอบ ขนส่ง'
                                : 'ค่าใช้จ่ายดูแลโครงการ';
                      return (
                        <tr key={c.code}>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: 2,
                                  background: c.tone,
                                }}
                              />
                              <b>{c.label}</b>
                            </span>
                          </td>
                          <td className="muted">{desc}</td>
                          <td className="num tnum">
                            <b>{fmt(amt)}</b>
                          </td>
                          <td>
                            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input type="checkbox" defaultChecked disabled={amt === 0} /> รวม
                            </label>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="total">
                      <td colSpan={2}>รวมต้นทุนทั้งหมด</td>
                      <td className="num tnum">{fmt(proj.total)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  padding: '14px',
                  background: '#e8eff8',
                  borderRadius: 6,
                  fontSize: 14,
                  lineHeight: 1.55,
                  display: 'flex',
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: 'var(--info)',
                    color: 'white',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  i
                </div>
                <div>
                  <b>
                    ระบบรวม Borrowing Cost ที่ Capitalize ไว้ {fmt(borrow)} บาท
                    ในต้นทุนทรัพย์สินอัตโนมัติ
                  </b>
                  <br />
                  ตาม TFRS 23 ดอกเบี้ยที่สะสมไว้ในโครงการต้องเป็นส่วนหนึ่งของราคาทุนของทรัพย์สิน
                </div>
              </div>
            </>
          )}

          {viewStep === 2 && (
            <>
              <div className="section-title" style={{ margin: '0 0 10px' }}>
                ขั้นตอนที่ 2 — แบ่งเป็นทรัพย์สินหลายตัว (ถ้าจำเป็น)
              </div>
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  marginBottom: 14,
                }}
              >
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}>#</th>
                      <th>ชื่อทรัพย์สิน</th>
                      <th>หมวด</th>
                      <th className="num">อายุ (ปี)</th>
                      <th className="num">มูลค่า</th>
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {splits.map((s, i) => (
                      <tr key={i}>
                        <td className="num">{i + 1}</td>
                        <td>
                          <input
                            className="input"
                            value={s.name}
                            onChange={(e) =>
                              setSplits(
                                splits.map((x, j) =>
                                  j === i ? { ...x, name: e.target.value } : x,
                                ),
                              )
                            }
                            style={{ width: '100%', height: 32, fontSize: 14 }}
                          />
                        </td>
                        <td>
                          <select
                            className="select"
                            value={s.cat}
                            onChange={(e) =>
                              setSplits(
                                splits.map((x, j) => (j === i ? { ...x, cat: e.target.value } : x)),
                              )
                            }
                            style={{ width: '100%', height: 32, fontSize: 14 }}
                          >
                            <option>เครื่องจักร</option>
                            <option>อาคารและสิ่งปลูกสร้าง</option>
                            <option>ยานพาหนะ</option>
                            <option>คอมพิวเตอร์</option>
                            <option>เฟอร์นิเจอร์</option>
                          </select>
                        </td>
                        <td>
                          <input
                            className="input tnum"
                            type="number"
                            value={s.life}
                            onChange={(e) =>
                              setSplits(
                                splits.map((x, j) =>
                                  j === i ? { ...x, life: parseInt(e.target.value, 10) || 0 } : x,
                                ),
                              )
                            }
                            style={{ width: 80, height: 32, fontSize: 14, textAlign: 'right' }}
                          />
                        </td>
                        <td>
                          <input
                            className="input tnum"
                            type="number"
                            value={Math.round(s.amt)}
                            onChange={(e) =>
                              setSplits(
                                splits.map((x, j) =>
                                  j === i ? { ...x, amt: parseFloat(e.target.value) || 0 } : x,
                                ),
                              )
                            }
                            style={{
                              width: 140,
                              height: 32,
                              fontSize: 14,
                              textAlign: 'right',
                              fontWeight: 600,
                            }}
                          />
                        </td>
                        <td>
                          <button
                            className="btn ghost"
                            style={{ height: 32, padding: '0 4px' }}
                            onClick={() => setSplits(splits.filter((_, j) => j !== i))}
                          >
                            <Icon name="x" size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={6} style={{ padding: 8 }}>
                        <button
                          className="btn sm"
                          onClick={() =>
                            setSplits([
                              ...splits,
                              { name: '', cat: 'เครื่องจักร', life: 10, amt: 0 },
                            ])
                          }
                        >
                          <Icon name="plus" size={12} /> เพิ่มทรัพย์สิน
                        </button>
                      </td>
                    </tr>
                    <tr className="total">
                      <td colSpan={4}>รวมแบ่ง</td>
                      <td className="num tnum">{fmt(totalSplits)}</td>
                      <td />
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  padding: '12px 14px',
                  background: Math.abs(diff) < 1 ? '#e7f4ec' : '#fce9e7',
                  border: `1px solid ${Math.abs(diff) < 1 ? '#bbdebf' : '#f4c1bd'}`,
                  borderRadius: 6,
                  fontSize: 14,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  <b>ต้นทุน CIP:</b> <span className="tnum">{fmt(proj.total)}</span> &nbsp;·&nbsp;{' '}
                  <b>รวมที่แบ่ง:</b> <span className="tnum">{fmt(totalSplits)}</span>
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    color: Math.abs(diff) < 1 ? 'var(--positive)' : 'var(--negative)',
                  }}
                >
                  ผลต่าง: {fmt(diff)} {Math.abs(diff) < 1 && '✓'}
                </span>
              </div>
            </>
          )}

          {viewStep === 3 && (
            <>
              <div className="section-title" style={{ margin: '0 0 10px' }}>
                ขั้นตอนที่ 3 — ใบสำคัญที่จะสร้างอัตโนมัติ
              </div>
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  overflow: 'hidden',
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    padding: '10px 14px',
                    background: '#fafafa',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <b>{headerVoucherNo} — โอน CIP เป็น Fixed Asset</b>
                  <span className="meta">วันที่ลงบัญชี: {fmtDate(headerCapitalDate)}</span>
                </div>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>บัญชี</th>
                      <th>คำอธิบาย</th>
                      <th className="num">เดบิต</th>
                      <th className="num">เครดิต</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positiveSplits.map((s, i) => (
                      <tr key={i}>
                        <td className="code">10220001</td>
                        <td>
                          FA — {s.name} ({s.cat}, {s.life} ปี)
                        </td>
                        <td className="num tnum">{fmt(Math.round(s.amt))}</td>
                        <td className="num">—</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="code">10260002</td>
                      <td>งานระหว่างก่อสร้าง — {proj.id} (โอนออก)</td>
                      <td className="num">—</td>
                      <td className="num tnum">{fmt(proj.total)}</td>
                    </tr>
                    <tr className="total">
                      <td colSpan={2}>รวม</td>
                      <td className="num tnum">{fmt(proj.total)}</td>
                      <td className="num tnum">{fmt(proj.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div
                style={{
                  padding: '14px 16px',
                  background: 'var(--brand-tint)',
                  border: '1px solid var(--brand-tint-2)',
                  borderLeft: '4px solid var(--brand)',
                  borderRadius: 8,
                }}
              >
                <div
                  className="section-title"
                  style={{ margin: '0 0 10px', color: 'var(--brand)' }}
                >
                  การกระทำหลังกด &quot;ส่งคำขออนุมัติ&quot;
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
                  <div>• ส่งคำขออนุมัติเข้าสายอนุมัติ 4-eyes (ผู้จัดทำ → ผู้อนุมัติ)</div>
                  <div>
                    • เมื่ออนุมัติครบทุกขั้น: สร้าง JV (Dr.FA / Cr.CIP) + ลงทะเบียน{' '}
                    <b>{positiveSplits.length} ทรัพย์สิน</b> ในระบบ Fixed Asset
                  </div>
                  <div>
                    • เริ่มคำนวณค่าเสื่อมเดือนถัดจากวันที่โอน + ปิด Project เป็น Capitalized
                  </div>
                </div>
              </div>

              {/* Phase 3b — once submitted (or if an open request already exists)
                  show the approval chain instead of the re-submit affordance.
                  Reuses the shared /ap ApprovalTimeline widget. */}
              {(submitted || openRequest) && (
                <div style={{ marginTop: 16 }} data-testid="cip-cap-approval">
                  {submitted && !openRequest && (
                    <div
                      style={{
                        background: '#e7f4ec',
                        border: '1px solid #bbdebf',
                        borderRadius: 6,
                        padding: '10px 14px',
                        fontSize: 14,
                        marginBottom: 12,
                      }}
                    >
                      ส่งคำขออนุมัติแล้ว — รออนุมัติ
                    </div>
                  )}
                  {openRequest && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: 10,
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          สายอนุมัติ — {openRequest.voucherNo}
                        </div>
                        {reqBadge && (
                          <span className={`badge ${reqBadge.tone}`}>
                            <span className="bdot" />
                            {reqBadge.label}
                          </span>
                        )}
                      </div>

                      {openRequest.approvalStatus === 'RETURNED' && openRequest.returnedReason && (
                        <div
                          style={{
                            background: 'var(--warning-bg)',
                            border: '1px solid var(--warning)',
                            borderRadius: 6,
                            padding: '10px 14px',
                            fontSize: 13,
                            marginBottom: 12,
                          }}
                          data-testid="cip-cap-returned-reason"
                        >
                          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
                          {openRequest.returnedReason}
                        </div>
                      )}

                      {reqPending && (
                        <div className="meta" style={{ marginBottom: 10 }}>
                          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {reqStep}/{reqTotalSteps}
                        </div>
                      )}

                      <ApprovalTimeline
                        approvals={(openRequest.approvals ?? []).map((a) => ({
                          id: a.stepNo,
                          stepNo: a.stepNo,
                          roleLabel: a.roleLabel,
                          approvedBy: a.approvedBy,
                          comment: a.comment,
                          approvedAt: a.approvedAt,
                        }))}
                        isActive={reqPending}
                        currentStep={reqStep}
                        emptyText="ยังไม่ส่งอนุมัติ"
                        leadLine={
                          openRequest.submittedBy ? (
                            <div className="meta">
                              ส่งอนุมัติโดย {openRequest.submittedBy}
                              {openRequest.submittedAt
                                ? ` · ${fmtDate(openRequest.submittedAt)}`
                                : ''}
                            </div>
                          ) : undefined
                        }
                        trailLine={
                          openRequest.status === 'CAPITALIZED' && openRequest.approvedBy ? (
                            <div className="meta">
                              อนุมัติโดย {openRequest.approvedBy}
                              {openRequest.approvedAt
                                ? ` · ${fmtDate(openRequest.approvedAt)}`
                                : ''}
                            </div>
                          ) : undefined
                        }
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 10,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            {submitted || openRequest ? 'ปิด' : 'ยกเลิก'}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {viewStep > 1 && !inApproval && (
              <button className="btn" onClick={() => setStep((step - 1) as 1 | 2 | 3)}>
                ← ย้อนกลับ
              </button>
            )}
            {viewStep < 3 ? (
              <button className="btn primary" onClick={() => setStep((step + 1) as 1 | 2 | 3)}>
                ขั้นถัดไป →
              </button>
            ) : (
              <div
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}
              >
                {capError && (
                  <span style={{ fontSize: 13, color: 'var(--negative)' }}>{capError}</span>
                )}
                {reqPending ? (
                  // An authorized approver can decide on the pending request here.
                  <button
                    className="btn primary"
                    onClick={() => setShowDecide(true)}
                    disabled={decideBusy}
                  >
                    <Icon name="check" size={14} /> พิจารณา
                  </button>
                ) : openRequest || submitted ? null : (
                  <button
                    className="btn primary"
                    onClick={submitCapitalize}
                    disabled={capMut.isPending}
                  >
                    <Icon name="check" size={14} />{' '}
                    {capMut.isPending ? 'กำลังส่งคำขออนุมัติ…' : 'ส่งคำขออนุมัติ (4-eyes)'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showDecide && openRequest && (
        <DecisionModal
          voucherNo={openRequest.voucherNo}
          step={reqStep || 1}
          busy={decideBusy}
          error={capError}
          titlePrefix="พิจารณาการโอนเป็นทรัพย์สิน"
          onConfirm={handleDecide}
          onClose={() => {
            setShowDecide(false);
            setCapError(null);
          }}
        />
      )}
    </div>
  );
}

interface ModalCloseProps {
  onClose: () => void;
}

type BorrowingPolicy = 'NONE' | 'SPECIFIC' | 'GENERAL_POOL';

const BORROWING_POLICY_OPTIONS: readonly { value: BorrowingPolicy; label: string }[] = [
  { value: 'NONE', label: 'ไม่ Capitalize ดอกเบี้ย' },
  { value: 'SPECIFIC', label: 'Specific — เงินกู้เฉพาะ' },
  { value: 'GENERAL_POOL', label: 'General Pool' },
];

function NewProjectModal({ onClose }: ModalCloseProps) {
  const companyId = useActiveCompanyId();
  const createMut = useCreateCipProject(companyId);
  const loansQuery = useLoans(companyId, true);
  const loans = loansQuery.data ?? [];
  const [name, setName] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('2026-06-01');
  const [expected, setExpected] = useState<string>('2027-05-31');
  const [budget, setBudget] = useState<string>('');
  const [pm, setPm] = useState<string>('');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [borrowingPolicy, setBorrowingPolicy] = useState<BorrowingPolicy>('NONE');
  const [tiedLoanId, setTiedLoanId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const loanLabel = (id: string) => {
    const l = loans.find((x) => x.id === id);
    return l ? `${l.code} — ${l.bank} (${Number(l.rate).toFixed(2)}%)` : id;
  };

  const submit = () => {
    if (!name.trim() || !code.trim()) {
      setError('กรุณากรอกชื่อโครงการและรหัสโครงการ');
      return;
    }
    if (borrowingPolicy === 'SPECIFIC' && !tiedLoanId) {
      setError('กรุณาเลือกเงินกู้เฉพาะ (Specific) ที่ผูกกับโครงการ');
      return;
    }
    setError(null);
    createMut.mutate(
      {
        code: code.trim(),
        name: name.trim(),
        pm: pm.trim() || null,
        budget: parseFloat(budget) || 0,
        branchId: branchId || null,
        departmentId: departmentId || null,
        startDate,
        expectedCompletionDate: expected || null,
        borrowingPolicy,
        tiedLoanId: borrowingPolicy === 'SPECIFIC' ? tiedLoanId : null,
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) =>
          setError(e instanceof Error ? e.message : 'สร้าง Project ไม่สำเร็จ'),
      },
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 680,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              สร้าง Project งานระหว่างก่อสร้างใหม่
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ระบบจะกำหนดเลขที่ <b style={{ color: '#3f3f46' }}>CIP-YYYY-NNNNNN</b> · เริ่มผูกบัญชี
              10260002 อัตโนมัติ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              ชื่อโครงการ
            </label>
            <input
              className="input"
              placeholder="เช่น ขยายโกดังภาคเหนือ เชียงใหม่"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                รหัสโครงการ (สั้น)
              </label>
              <input
                className="input"
                placeholder="เช่น KILN-04"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา
              </label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                หน่วยงาน
              </label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Project Manager
              </label>
              <input
                className="input"
                placeholder="ชื่อผู้รับผิดชอบ"
                value={pm}
                onChange={(e) => setPm(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันเริ่ม
              </label>
              <input
                className="input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                คาดเสร็จ
              </label>
              <input
                className="input"
                type="date"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                งบประมาณ (บาท)
              </label>
              <input
                className="input tnum"
                type="number"
                placeholder="0.00"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: '#fce9e7',
                border: '1px solid #f4c1bd',
                borderRadius: 6,
                fontSize: 14,
                color: 'var(--negative)',
              }}
            >
              {error}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                นโยบายดอกเบี้ย (TFRS 23)
              </label>
              <select
                className="select"
                style={{ width: '100%', height: 38, fontSize: 14 }}
                value={borrowingPolicy}
                onChange={(e) => setBorrowingPolicy(e.target.value as BorrowingPolicy)}
              >
                {BORROWING_POLICY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {borrowingPolicy === 'SPECIFIC' && (
              <div>
                <label
                  style={{
                    fontSize: 14,
                    color: 'var(--text-muted)',
                    fontWeight: 500,
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  เงินกู้เฉพาะที่ผูกกับโครงการ
                </label>
                <SearchSelect
                  value={tiedLoanId}
                  onChange={setTiedLoanId}
                  options={loans.map((l) => ({
                    value: l.id,
                    label: `${l.code} — ${l.bank} (${Number(l.rate).toFixed(2)}%)`,
                  }))}
                  getLabel={loanLabel}
                  placeholder="เลือกเงินกู้…"
                />
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={createMut.isPending || !companyId}
          >
            <Icon name="check" size={14} /> {createMut.isPending ? 'กำลังบันทึก…' : 'สร้าง Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface BorrowingRunModalProps {
  year: string;
  month: string;
  onClose: () => void;
}

function BorrowingRunModal({ year, month, onClose }: BorrowingRunModalProps) {
  const companyId = useActiveCompanyId();
  const period = `${year}${month}`;
  const monthLabel = `${thaiMonthLabel(month)} ${year}`;
  const capRateQuery = useCipCapRate(companyId, period);
  const previewQuery = useCipBorrowingPreview(companyId, period);
  const runsQuery = useCipBorrowingRuns(companyId, period);
  const runMut = useRunCipBorrowing(companyId);
  const approveMut = useApproveCipBorrowingRun(companyId);
  const rejectMut = useRejectCipBorrowingRun(companyId);
  const returnMut = useReturnCipBorrowingRun(companyId);
  const [error, setError] = useState<string | null>(null);
  const [showDecide, setShowDecide] = useState(false);

  const capRate = Number(capRateQuery.data?.capRate ?? 0);
  const previewRows = previewQuery.data ?? [];
  const lineCount = previewRows.length;
  const totalCapAmt = previewRows.reduce((s, r) => s + Number(r.capitalizable), 0);

  // The live run for this period (CANCELLED runs don't block a re-submit). Once
  // it exists we switch the modal into the approval-chain view (Phase 3b 4-eyes).
  const activeRun = (runsQuery.data ?? []).find((r) => r.status !== 'CANCELLED');
  const isPending = activeRun?.approvalStatus === 'PENDING';
  const currentStep = activeRun?.currentStep ?? 0;
  const totalSteps = activeRun?.approvals?.length ?? 0;
  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const runBadge = activeRun ? cipApprovalBadge(activeRun.approvalStatus, activeRun.status) : null;

  const submit = () => {
    if (!companyId) {
      onClose();
      return;
    }
    if (totalCapAmt <= 0) {
      setError('ไม่มีดอกเบี้ยที่ต้อง Capitalize ในงวดนี้');
      return;
    }
    setError(null);
    runMut.mutate(
      { period },
      {
        // After submit the run is PENDING — keep the modal open so the approval
        // chain renders (the runs query re-fetches via invalidation).
        onError: (e: unknown) =>
          setError(e instanceof Error ? e.message : 'ส่งอนุมัติดอกเบี้ยไม่สำเร็จ'),
      },
    );
  };

  const handleDecide = (d: Decision) => {
    if (!activeRun) return;
    setError(null);
    const onError = (e: unknown) => setError(e instanceof Error ? e.message : 'ดำเนินการไม่สำเร็จ');
    const onSuccess = () => setShowDecide(false);
    if (d.action === 'approve') {
      approveMut.mutate(
        {
          runId: activeRun.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        },
        { onSuccess, onError },
      );
    } else if (d.action === 'return') {
      returnMut.mutate(
        {
          runId: activeRun.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        },
        { onSuccess, onError },
      );
    } else {
      rejectMut.mutate(
        {
          runId: activeRun.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        },
        { onSuccess, onError },
      );
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 760,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              คำนวณดอกเบี้ย Capitalize — งวด {monthLabel}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ระบบจะรวบรวม Avg CIP × Capitalization Rate แล้วส่งอนุมัติ 4-eyes — JV
              จะถูกบันทึกเมื่ออนุมัติครบทุกขั้น
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 14,
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              marginBottom: 14,
            }}
          >
            <div>
              <div className="meta">โครงการที่จะ Capitalize</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
              >
                {lineCount}
              </div>
            </div>
            <div>
              <div className="meta">Cap Rate ที่ใช้</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                  color: 'var(--brand)',
                }}
              >
                {capRate.toFixed(2)}%
              </div>
            </div>
            <div>
              <div className="meta">ดอกเบี้ยรวมที่จะ Capitalize</div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginTop: 4,
                  color: '#7030A0',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(totalCapAmt, 0)}
              </div>
            </div>
          </div>
          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <b>
              ระบบจะสร้าง JV (Dr. 10260002 CIP แยก {lineCount} Project / Cr. 60200004 ดอกเบี้ยจ่าย)
            </b>{' '}
            รวม {lineCount} รายการ สำหรับงวด {monthLabel} — JV จะถูกบันทึกเมื่ออนุมัติครบทุกขั้น
          </div>

          {/* Phase 3b approval chain — once the run is submitted it walks the
              central engine (maker → reviewer(s) → approver); the JVs post on
              final approval. Reuses the shared /ap ApprovalTimeline widget. */}
          {activeRun && (
            <div style={{ marginTop: 16 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 10,
                }}
              >
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  สายอนุมัติ — {activeRun.voucherNo}
                </div>
                {runBadge && (
                  <span className={`badge ${runBadge.tone}`}>
                    <span className="bdot" />
                    {runBadge.label}
                  </span>
                )}
              </div>

              {activeRun.approvalStatus === 'RETURNED' && activeRun.returnedReason && (
                <div
                  style={{
                    background: 'var(--warning-bg)',
                    border: '1px solid var(--warning)',
                    borderRadius: 6,
                    padding: '10px 14px',
                    fontSize: 13,
                    marginBottom: 12,
                  }}
                  data-testid="cip-run-returned-reason"
                >
                  <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
                  {activeRun.returnedReason}
                </div>
              )}

              {isPending && (
                <div className="meta" style={{ marginBottom: 10 }}>
                  <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
                </div>
              )}

              <ApprovalTimeline
                approvals={(activeRun.approvals ?? []).map((a) => ({
                  id: a.stepNo,
                  stepNo: a.stepNo,
                  roleLabel: a.roleLabel,
                  approvedBy: a.approvedBy,
                  comment: a.comment,
                  approvedAt: a.approvedAt,
                }))}
                isActive={isPending}
                currentStep={currentStep}
                emptyText="ยังไม่ส่งอนุมัติ"
                leadLine={
                  activeRun.submittedBy ? (
                    <div className="meta">
                      ส่งอนุมัติโดย {activeRun.submittedBy}
                      {activeRun.submittedAt ? ` · ${fmtDate(activeRun.submittedAt)}` : ''}
                    </div>
                  ) : undefined
                }
                trailLine={
                  activeRun.status === 'POSTED' && activeRun.approvedBy ? (
                    <div className="meta">
                      อนุมัติโดย {activeRun.approvedBy}
                      {activeRun.approvedAt ? ` · ${fmtDate(activeRun.approvedAt)}` : ''}
                    </div>
                  ) : undefined
                }
              />
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            {error && <span style={{ fontSize: 13, color: 'var(--negative)' }}>{error}</span>}
            {isPending ? (
              <button
                className="btn primary"
                onClick={() => setShowDecide(true)}
                disabled={decideBusy}
              >
                <Icon name="check" size={14} /> พิจารณา
              </button>
            ) : activeRun && activeRun.status !== 'CANCELLED' && !isPending ? null : (
              <button
                className="btn primary"
                onClick={submit}
                disabled={runMut.isPending || previewQuery.isPending || !companyId}
              >
                <Icon name="check" size={14} />{' '}
                {runMut.isPending ? 'กำลังส่งอนุมัติ…' : 'ส่งอนุมัติ (4-eyes)'}
              </button>
            )}
          </div>
        </div>
      </div>

      {showDecide && activeRun && (
        <DecisionModal
          voucherNo={activeRun.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={error}
          titlePrefix="พิจารณาการ Capitalize ดอกเบี้ย"
          onConfirm={handleDecide}
          onClose={() => {
            setShowDecide(false);
            setError(null);
          }}
        />
      )}
    </div>
  );
}

interface WriteOffModalProps {
  proj: CipProject;
  onClose: () => void;
}

function WriteOffModal({ proj, onClose }: WriteOffModalProps) {
  const companyId = useActiveCompanyId();
  const writeOffMut = useWriteOffCipProject(companyId);
  const [writeOffDate, setWriteOffDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState<string>('');
  const [writeOffAccountCode, setWriteOffAccountCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!proj.apiId || !companyId) {
      onClose();
      return;
    }
    if (!reason.trim()) {
      setError('กรุณาระบุเหตุผลในการตัดจำหน่าย');
      return;
    }
    setError(null);
    writeOffMut.mutate(
      {
        cipProjectId: proj.apiId,
        data: {
          writeOffDate,
          reason: reason.trim(),
          ...(writeOffAccountCode.trim()
            ? { writeOffAccountCode: writeOffAccountCode.trim() }
            : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => setError(e instanceof Error ? e.message : 'ตัดจำหน่ายไม่สำเร็จ'),
      },
    );
  };

  return (
    <AppModal
      title={`ตัดจำหน่ายงานระหว่างก่อสร้าง — ${proj.id}`}
      subtitle={`${proj.name} · ต้นทุนรวม ${fmt(proj.total)} บาท`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary danger"
            onClick={submit}
            disabled={writeOffMut.isPending || !companyId}
          >
            <Icon name="x" size={14} />{' '}
            {writeOffMut.isPending ? 'กำลังตัดจำหน่าย…' : 'ยืนยันตัดจำหน่าย'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            padding: '12px 14px',
            background: '#fce9e7',
            border: '1px solid #f4c1bd',
            borderRadius: 6,
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          ระบบจะสร้าง JV <b>Dr. ค่าใช้จ่าย / Cr. 10260002 CIP</b> ตามต้นทุนสะสม{' '}
          <b>{fmt(proj.total)}</b> บาท และเปลี่ยนสถานะโครงการเป็น <b>ยกเลิก (CANCELLED)</b>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              วันที่ตัดจำหน่าย
            </div>
            <input
              className="input"
              type="date"
              value={writeOffDate}
              onChange={(e) => setWriteOffDate(e.target.value)}
              style={{ width: '100%', height: 38 }}
            />
          </label>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              บัญชีค่าใช้จ่าย (ไม่บังคับ)
            </div>
            <input
              className="input code"
              value={writeOffAccountCode}
              onChange={(e) => setWriteOffAccountCode(e.target.value)}
              style={{ width: '100%', height: 38 }}
              placeholder="ค่าเริ่มต้น 60110042"
            />
          </label>
        </div>
        <label>
          <div className="meta" style={{ marginBottom: 6 }}>
            เหตุผลในการตัดจำหน่าย
          </div>
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', height: 38 }}
            placeholder="เช่น ยกเลิกโครงการ / โครงการไม่คุ้มทุน"
          />
        </label>
        {error && (
          <div
            style={{
              padding: '10px 12px',
              background: '#fce9e7',
              border: '1px solid #f4c1bd',
              borderRadius: 6,
              fontSize: 14,
              color: 'var(--negative)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AppModal>
  );
}

interface SuspendModalProps {
  proj: CipProject;
  onClose: () => void;
}

// Phase 5 (IAS 23.20) — pause borrowing-cost capitalization while active
// development is halted. Stamps suspendDate + a required reason; the TFRS 23
// engine then excludes the project from the next period's allocation.
function SuspendModal({ proj, onClose }: SuspendModalProps) {
  const companyId = useActiveCompanyId();
  const suspendMut = useSuspendCipProject(companyId);
  const [suspendDate, setSuspendDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    if (!proj.apiId || !companyId) {
      onClose();
      return;
    }
    if (!reason.trim()) {
      setError('กรุณาระบุเหตุผลในการระงับ');
      return;
    }
    setError(null);
    suspendMut.mutate(
      {
        cipProjectId: proj.apiId,
        data: { suspendDate, reason: reason.trim() },
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => setError(e instanceof Error ? e.message : 'ระงับไม่สำเร็จ'),
      },
    );
  };

  return (
    <AppModal
      title={`ระงับชั่วคราว — ${proj.id}`}
      subtitle={`${proj.name} · IAS 23.20 หยุดรวมดอกเบี้ยเป็นทุนระหว่างที่หยุดพัฒนา`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={suspendMut.isPending || !companyId}
          >
            <Icon name="x" size={14} /> {suspendMut.isPending ? 'กำลังระงับ…' : 'ยืนยันระงับ'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div
          style={{
            padding: '12px 14px',
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            borderRadius: 6,
            fontSize: 14,
            lineHeight: 1.55,
            color: '#b54708',
          }}
        >
          ระหว่างระงับ ระบบ <b>จะไม่รวมดอกเบี้ยเป็นทุน (Capitalize)</b> ของโครงการนี้ในงวดถัดไป
          ตามมาตรฐาน TFRS 23 — กด &quot;กลับมาทำต่อ&quot; เมื่อเริ่มก่อสร้างอีกครั้ง
        </div>
        <label>
          <div className="meta" style={{ marginBottom: 6 }}>
            วันที่ระงับ
          </div>
          <input
            className="input"
            type="date"
            value={suspendDate}
            onChange={(e) => setSuspendDate(e.target.value)}
            style={{ width: '100%', height: 38 }}
          />
        </label>
        <label>
          <div className="meta" style={{ marginBottom: 6 }}>
            เหตุผลในการระงับ
          </div>
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ width: '100%', height: 38 }}
            placeholder="เช่น รอเงินทุน / หยุดงานก่อสร้าง / ข้อพิพาทผู้รับเหมา"
          />
        </label>
        {error && (
          <div
            style={{
              padding: '10px 12px',
              background: '#fce9e7',
              border: '1px solid #f4c1bd',
              borderRadius: 6,
              fontSize: 14,
              color: 'var(--negative)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AppModal>
  );
}

interface AddCostEntryModalProps {
  proj: CipProject;
  onClose: () => void;
}

function AddCostEntryModal({ proj, onClose }: AddCostEntryModalProps) {
  const companyId = useActiveCompanyId();
  const addMut = useAddCipCostEntry(companyId);
  const [entryDate, setEntryDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState<string>('');
  const [accountCode, setAccountCode] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [pool, setPool] = useState<PoolCode>('DIRECT_CONS');
  const [isCapitalizable, setIsCapitalizable] = useState<boolean>(true);
  const [vendorName, setVendorName] = useState<string>('');
  const [sourceRef, setSourceRef] = useState<string>('');
  // Phase 4 — optional contractor withholdings (off-CIP; only `amount` capitalizes).
  const [showContractor, setShowContractor] = useState<boolean>(false);
  const [vatAmount, setVatAmount] = useState<string>('');
  const [whtAmount, setWhtAmount] = useState<string>('');
  const [retentionAmount, setRetentionAmount] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const amtNum = parseFloat(amount) || 0;
  const vatNum = parseFloat(vatAmount) || 0;
  const whtNum = parseFloat(whtAmount) || 0;
  const retNum = parseFloat(retentionAmount) || 0;
  // Net to vendor = work value + VAT − WHT − retention (the Cr source line).
  const netToVendor = amtNum + vatNum - whtNum - retNum;

  const submit = () => {
    const amt = parseFloat(amount);
    if (!description.trim() || !accountCode.trim() || !(amt > 0)) {
      setError('กรุณากรอกคำอธิบาย รหัสบัญชี และจำนวนเงิน (> 0)');
      return;
    }
    if (netToVendor < 0) {
      setError('ยอดจ่ายสุทธิติดลบ — ตรวจสอบ VAT / หัก ณ ที่จ่าย / เงินประกันผลงาน');
      return;
    }
    setError(null);
    addMut.mutate(
      {
        cipProjectId: proj.apiId,
        data: {
          entryDate,
          description: description.trim(),
          accountCode: accountCode.trim(),
          amount: amt,
          pool,
          isCapitalizable,
          isBorrowingCost: pool === 'BORROW',
          vendorName: vendorName.trim() || null,
          sourceRef: sourceRef.trim() || null,
          // Omit when 0/blank → the API keeps the simple 2-line JV path.
          ...(vatNum > 0 ? { vatAmount: vatNum } : {}),
          ...(whtNum > 0 ? { whtAmount: whtNum } : {}),
          ...(retNum > 0 ? { retentionAmount: retNum } : {}),
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) => setError(e instanceof Error ? e.message : 'บันทึกต้นทุนไม่สำเร็จ'),
      },
    );
  };

  return (
    <AppModal
      title={`เพิ่มต้นทุนเข้าโครงการ — ${proj.id}`}
      subtitle={proj.name}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={addMut.isPending || !companyId}
          >
            <Icon name="check" size={14} /> {addMut.isPending ? 'กำลังบันทึก…' : 'บันทึกต้นทุน'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              วันที่
            </div>
            <input
              className="input"
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              style={{ width: '100%', height: 38 }}
            />
          </label>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              จำนวนเงิน (บาท)
            </div>
            <input
              className="input tnum"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: '100%', height: 38, textAlign: 'right' }}
              placeholder="0.00"
            />
          </label>
        </div>
        <label>
          <div className="meta" style={{ marginBottom: 6 }}>
            คำอธิบายรายการ
          </div>
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: '100%', height: 38 }}
            placeholder="เช่น ค่าก่อสร้างฐานราก งวด 1"
          />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              กองต้นทุน (Cost Pool)
            </div>
            <SearchSelect
              value={pool}
              onChange={(v) => setPool(v as PoolCode)}
              options={COST_POOLS.map((c) => ({ value: c.code, label: c.label }))}
              getLabel={(v) => COST_POOLS.find((c) => c.code === v)?.label ?? v}
            />
          </label>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              รหัสบัญชีต้นทุน/ค่าใช้จ่าย
            </div>
            <input
              className="input code"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              style={{ width: '100%', height: 38 }}
              placeholder="เช่น 60110042"
            />
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              ผู้ขาย / ผู้รับเหมา
            </div>
            <input
              className="input"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              style={{ width: '100%', height: 38 }}
              placeholder="ชื่อผู้ขาย (ไม่บังคับ)"
            />
          </label>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              เลขที่อ้างอิง (PV/สัญญา)
            </div>
            <input
              className="input code"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              style={{ width: '100%', height: 38 }}
              placeholder="ไม่บังคับ"
            />
          </label>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input
            type="checkbox"
            checked={isCapitalizable}
            onChange={(e) => setIsCapitalizable(e.target.checked)}
          />
          รวมเป็นราคาทุนทรัพย์สิน (Dr งานระหว่างก่อสร้าง). หากไม่ติ๊ก = ตัดเป็นค่าใช้จ่ายทันที
        </label>

        {/* Phase 4 — optional contractor section: VAT / WHT / retention. Collapsed
            by default so the simple direct-cost flow stays clean. */}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => setShowContractor((s) => !s)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              background: '#fafafa',
              border: 'none',
              borderBottom: showContractor ? '1px solid var(--border)' : 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            <Icon name={showContractor ? 'chevronD' : 'chevron'} size={14} />
            ผู้รับเหมา — หัก ณ ที่จ่าย / เงินประกันผลงาน
            <span className="meta" style={{ marginLeft: 'auto', fontWeight: 400 }}>
              ไม่บังคับ
            </span>
          </button>
          {showContractor && (
            <div
              style={{ padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={() => setVatAmount((amtNum * 0.07).toFixed(2))}
                >
                  VAT 7%
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={() => setWhtAmount((amtNum * 0.03).toFixed(2))}
                >
                  หัก ณ ที่จ่าย 3%
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  onClick={() => setRetentionAmount((amtNum * 0.05).toFixed(2))}
                >
                  เงินประกันผลงาน 5%
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                <label>
                  <div className="meta" style={{ marginBottom: 6 }}>
                    ภาษีซื้อ (VAT)
                  </div>
                  <input
                    className="input tnum"
                    type="number"
                    value={vatAmount}
                    onChange={(e) => setVatAmount(e.target.value)}
                    style={{ width: '100%', height: 38, textAlign: 'right' }}
                    placeholder="0.00"
                  />
                </label>
                <label>
                  <div className="meta" style={{ marginBottom: 6 }}>
                    ภาษีหัก ณ ที่จ่าย (WHT)
                  </div>
                  <input
                    className="input tnum"
                    type="number"
                    value={whtAmount}
                    onChange={(e) => setWhtAmount(e.target.value)}
                    style={{ width: '100%', height: 38, textAlign: 'right' }}
                    placeholder="0.00"
                  />
                </label>
                <label>
                  <div className="meta" style={{ marginBottom: 6 }}>
                    เงินประกันผลงาน (Retention)
                  </div>
                  <input
                    className="input tnum"
                    type="number"
                    value={retentionAmount}
                    onChange={(e) => setRetentionAmount(e.target.value)}
                    style={{ width: '100%', height: 38, textAlign: 'right' }}
                    placeholder="0.00"
                  />
                </label>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  background: '#e8eff8',
                  borderRadius: 6,
                  fontSize: 14,
                }}
              >
                <span>
                  ยอดจ่ายสุทธิ (net to vendor) = ราคางาน + VAT − หัก ณ ที่จ่าย − ประกันผลงาน
                </span>
                <b
                  className="tnum"
                  style={{ color: netToVendor < 0 ? 'var(--negative)' : 'var(--brand)' }}
                >
                  {fmt(netToVendor)}
                </b>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: '10px 12px',
              background: '#fce9e7',
              border: '1px solid #f4c1bd',
              borderRadius: 6,
              fontSize: 14,
              color: 'var(--negative)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AppModal>
  );
}

// Phase 4 — release retention back to the contractor (Dr RETENTION_PAYABLE / Cr
// cash). Guards amount ≤ outstanding client-side; the server re-validates.
interface ReleaseRetentionModalProps {
  proj: CipProject;
  outstanding: number;
  onClose: () => void;
}

function ReleaseRetentionModal({ proj, outstanding, onClose }: ReleaseRetentionModalProps) {
  const companyId = useActiveCompanyId();
  const releaseMut = useReleaseCipRetention(companyId);
  const [vendorName, setVendorName] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [releaseDate, setReleaseDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const amt = parseFloat(amount);
    if (!(amt > 0)) {
      setError('กรุณากรอกจำนวนเงินคืน (> 0)');
      return;
    }
    if (amt > outstanding) {
      setError(`คืนได้ไม่เกินยอดคงค้าง ${fmt(outstanding)} บาท`);
      return;
    }
    setError(null);
    releaseMut.mutate(
      {
        cipProjectId: proj.apiId,
        data: {
          amount: amt,
          releaseDate,
          vendorName: vendorName.trim() || null,
        },
      },
      {
        onSuccess: () => onClose(),
        onError: (e: unknown) =>
          setError(e instanceof Error ? e.message : 'คืนเงินประกันไม่สำเร็จ'),
      },
    );
  };

  return (
    <AppModal
      title={`คืนเงินประกันผลงาน — ${proj.id}`}
      subtitle={`คงค้าง ${fmt(outstanding)} บาท`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={releaseMut.isPending || !companyId}
          >
            <Icon name="check" size={14} />{' '}
            {releaseMut.isPending ? 'กำลังบันทึก…' : 'คืนเงินประกัน'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              วันที่คืน
            </div>
            <input
              className="input"
              type="date"
              value={releaseDate}
              onChange={(e) => setReleaseDate(e.target.value)}
              style={{ width: '100%', height: 38 }}
            />
          </label>
          <label>
            <div className="meta" style={{ marginBottom: 6 }}>
              จำนวนเงินคืน (บาท)
            </div>
            <input
              className="input tnum"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: '100%', height: 38, textAlign: 'right' }}
              placeholder="0.00"
            />
          </label>
        </div>
        <label>
          <div className="meta" style={{ marginBottom: 6 }}>
            ผู้รับเหมา (ไม่บังคับ)
          </div>
          <input
            className="input"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            style={{ width: '100%', height: 38 }}
            placeholder="ชื่อผู้รับเหมา"
          />
        </label>
        {error && (
          <div
            style={{
              padding: '10px 12px',
              background: '#fce9e7',
              border: '1px solid #f4c1bd',
              borderRadius: 6,
              fontSize: 14,
              color: 'var(--negative)',
            }}
          >
            {error}
          </div>
        )}
      </div>
    </AppModal>
  );
}
